import './bootstrap';
import './amp-monitoring';
import { beginCheckoutInitialization, markStripeInitializationFailure, stripeInitializationDiagnostics, stripeInitializationStage, stripeLoadErrorFired } from './checkout-stripe-readiness';
import { ensureCheckoutPaymentSdks, ensurePayPalSdk, ensureStripeSdk, resolvePayPalClientId, resolvePayPalVault } from './payment-sdk-loader';
import { mountCheckoutPaymentSdks, teardownCheckoutPaymentWidgets } from './checkout-payment-mount';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import {
	applyBillingDetailsFromDom,
	hydrateBillingDetailsFromDom,
	loadBillingDetailsFromDom,
	parseAlpineJson,
	paymentAlpineOptions,
	savedPayerBilling,
	watchBillingDetailsHosts,
} from './billing-details-from-dom';
import { registerLivewireSessionExpiryHook } from './livewire-session-expiry';
import {
	livewireEventMessage,
	payerBillingDetails,
	payerConfirmParams,
	paymentCspViolationSnapshot,
	readCreditApplyFlagsFromWire,
	recordPaymentCspViolation,
	shouldAbortStripeConfirmAfterCreditApply,
	shouldApplyCreditsBeforeStripeConfirm,
	stripeConfirmExceptionPayload,
	stripeElementsSessionChanged,
	stripeSecretFingerprint,
} from './stripe-confirm-helpers';

window.loadBillingDetailsFromDom = loadBillingDetailsFromDom;
window.parseAlpineJson = parseAlpineJson;
window.applyBillingDetailsFromDom = applyBillingDetailsFromDom;
window.hydrateBillingDetailsFromDom = hydrateBillingDetailsFromDom;
window.watchBillingDetailsHosts = watchBillingDetailsHosts;
window.paymentAlpineOptions = paymentAlpineOptions;
window.savedPayerBilling = savedPayerBilling;

document.addEventListener('alpine:init', () => {
	window.Alpine?.data?.('savedPayerBilling', savedPayerBilling);
});

const registerLivewireBillingHydrate = () => {
	if (registerLivewireBillingHydrate.done || ! window.Livewire?.hook) {
		return;
	}

	registerLivewireBillingHydrate.done = true;
	registerLivewireSessionExpiryHook(window.Livewire);
	window.Livewire.hook('morph.updated', () => hydrateBillingDetailsFromDom());
	window.Livewire.hook('commit', ({ succeed }) => {
		succeed(() => hydrateBillingDetailsFromDom());
	});
};

document.addEventListener('livewire:init', registerLivewireBillingHydrate);
registerLivewireBillingHydrate();

// Uppy uses private class fields (`#store`) and Alpine wraps component data
// in a reactive Proxy. Storing a Uppy instance on `this` inside Alpine.data()
// causes "Cannot read from private field" because Uppy's methods then access
// their own private slots through the proxied reference. Keep Uppy instances
// completely outside Alpine's reactive graph by holding them in a module-
// scoped WeakMap keyed by the Alpine component proxy.
const uppyByComponent = new WeakMap();
let checkoutEnforcedScriptCspViolationSeen = false;
document.addEventListener('securitypolicyviolation', (event) => {
	if (event.disposition === 'enforce' && event.effectiveDirective?.startsWith('script-src')) {
		checkoutEnforcedScriptCspViolationSeen = true;
	}
	recordPaymentCspViolation(event);
});

const livewireStripeFailurePayload = (error, flow) => ({
	message: error?.message ?? 'Unknown error',
	gateway: 'stripe',
	code: error?.code ?? null,
	declineCode: error?.decline_code ?? null,
	type: error?.type ?? null,
	paymentIntentId: error?.payment_intent?.id ?? null,
	setupIntentId: error?.setup_intent?.id ?? null,
	flow,
});

/**
 * Call applyCreditsAndPay only when Locked creditsAppliedAmountValue > 0.
 * A $0-credit Livewire round trip morphs the payment section and destroys
 * Stripe/PayPal iframes (elements_remounted). Livewire can still resolve
 * even when PHP caught an apply failure — abort confirmPayment then so we
 * never charge against a stale remainder.
 */
const abortStripeConfirmIfCreditApplyFailed = async (component, flow) => {
	const wireGet = (key) => component.$wire?.get?.(key);
	const flags = readCreditApplyFlagsFromWire(wireGet, {
		useCredits: component.useCredits,
		remainingAfterCredits: component.remainingAfterCredits,
		creditsAppliedAmount: component.creditsAppliedAmount,
	});

	if (! shouldApplyCreditsBeforeStripeConfirm(flags)) {
		return { abort: false, reason: null };
	}

	let result = null;
	try {
		result = await component.$wire.applyCreditsAndPay();
	} catch (error) {
		result = {
			ok: false,
			error: error?.message ?? "We couldn't apply your account credits. Please try again.",
		};
	}

	const decision = shouldAbortStripeConfirmAfterCreditApply(result, {
		expectedCreditsApplied: flags.creditsAppliedAmount,
	});

	if (! decision.abort) {
		return decision;
	}

	if (decision.reason === 'fully_paid') {
		component.setButtonState?.('success', 'Payment complete! Redirecting you now...');

		return decision;
	}

	component.setButtonState?.('error');
	component.$wire?.dispatch?.('paymentFailed', {
		message: result?.error || "We couldn't apply your account credits. Please try again.",
		gateway: 'stripe',
		code: 'credits_apply_failed',
		flow,
		paymentIntentId: component.$wire?.get?.('stripePaymentIntentId') ?? null,
	});
	setTimeout(() => component.setButtonState?.('ready'), 3000);

	return decision;
};

const livewirePayPalFailurePayload = (err, flow, orderId = null) => {
	let paypalErr = null;
	let message = 'PayPal error';
	if (err != null && err !== '') {
		if (typeof err === 'string') {
			message = err.slice(0, 500);
			paypalErr = message;
		} else {
			message = err?.message ? String(err.message).slice(0, 500) : message;
			try {
				paypalErr = JSON.stringify(err).slice(0, 500);
			} catch {
				paypalErr = String(err).slice(0, 500);
			}
		}
	}

	return {
		message,
		gateway: 'paypal',
		paypalErr,
		paypalOrderId: orderId,
		flow,
	};
};

// Note: Alpine is bundled with Livewire 4 and auto-started.
// The @alpinejs/focus plugin is included by default in Livewire 4.

/**
 * Always (re)register payment Alpine.data factories that alpine-csp-boot stubs.
 *
 * Register on alpine:init whenever this module has already evaluated. If Vite
 * deferred the module until after Alpine started, alpine-csp-boot upgrades the
 * in-place stub on `payment-alpine-ready` — never destroyTree on Livewire DOM.
 */
const registerPaymentAlpineData = () => {
	if (!window.Alpine) {
		return;
	}

	if (typeof window.paymentMethodForm !== 'function'
		|| typeof window.invoicePaymentManager !== 'function'
		|| typeof window.checkoutPayment !== 'function') {
		return;
	}

	const Alpine = window.Alpine;

	Alpine.data('paymentMethodForm', (clientSecret, stripeKey) => window.paymentMethodForm(
		clientSecret,
		stripeKey,
	));
	Alpine.data('invoicePaymentManager', (
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		invoicesListUrl,
	) => window.invoicePaymentManager({
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		invoicesListUrl,
	}));
	Alpine.data('checkoutPayment', (
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		initialPaymentMethod,
		stripeSetupClientSecret,
		isSetupMode,
	) => window.checkoutPayment({
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		initialPaymentMethod,
		stripeSetupClientSecret,
		isSetupMode,
	}));
	if (typeof window.creditsPurchase === 'function') {
		Alpine.data('creditsPurchase', (clientSecret, paymentMethod, stripeKey, paypalClientId) => window.creditsPurchase(
			clientSecret,
			paymentMethod,
			stripeKey,
			paypalClientId,
		));
	}
};

const registerAlpineComponents = () => {
	if (registerAlpineComponents.registered || !window.Alpine) {
		return;
	}

	registerAlpineComponents.registered = true;

	const Alpine = window.Alpine;

	Alpine.data('savedPayerBilling', savedPayerBilling);

	Alpine.data('timerDisplay', (startedAt) => ({
		elapsed: '0:00:00',
		startTime: null,
		interval: null,

		init() {
			if (startedAt) {
				this.startTime = new Date(startedAt);
				this.updateElapsed();
				this.interval = setInterval(() => this.updateElapsed(), 1000);
			}
		},

		updateElapsed() {
			if (!this.startTime) {
				return;
			}

			const now = new Date();
			const diff = Math.floor((now - this.startTime) / 1000);

			const hours = Math.floor(diff / 3600);
			const minutes = Math.floor((diff % 3600) / 60);
			const seconds = diff % 60;

			this.elapsed = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
		},

		destroy() {
			if (this.interval) {
				clearInterval(this.interval);
			}
		},
	}));

	// CSP-safe Alpine: Blade may only pass scalars in x-data. Locations JSON
	// is read from data-locations on the host element inside init().
	// Brand-safe probing: solid initial median, gentle 10s refresh while Configure
	// is open, Auto hysteresis so winners don't thrash on noise.
	window.locationLatencyPicker = (itemIndex) => ({
		locations: [],
		itemIndex: Number(itemIndex) || 0,
		results: {},
		measuring: false,
		autoResolvedId: null,
		autoMode: 'default',
		syncedId: null,
		pendingChallengerId: null,
		pendingChallengerRounds: 0,
		_morphHook: null,
		_refreshTimer: null,
		_probeInFlight: false,
		_onVisibility: null,

		initialSampleCount: 4,
		refreshSampleCount: 2,
		maxSampleWindow: 5,
		refreshIntervalMs: 10000,
		hysteresisMs: 25,
		hysteresisRounds: 2,

		get isAutoSelected() {
			const locationId = this.$wire?.get(`itemConfigurations.${this.itemIndex}.location_id`);
			return locationId === null || locationId === '' || locationId === undefined;
		},

		get isManualSelected() {
			return !this.isAutoSelected;
		},

		get hasLatencyResults() {
			return Object.keys(this.results || {}).length > 0;
		},

		get latencyRows() {
			return (this.locations || []).map((loc) => ({
				id: loc.id,
				label: loc.label,
				text: this.latencyLabel(loc.id),
				className: this.latencyClass(loc.id),
			}));
		},

		get autoLocation() {
			if (!this.autoResolvedId) {
				return this.defaultLocation();
			}

			return this.locations.find((loc) => Number(loc.id) === Number(this.autoResolvedId)) || this.defaultLocation();
		},

		get autoHeadline() {
			const loc = this.autoLocation;
			if (!loc) {
				return 'Auto selected: unavailable';
			}

			const ms = this.results[loc.id]?.ms;
			if (this.autoMode === 'latency' && ms != null) {
				return this.measuring
					? `Auto selected: ${loc.label} · ~${ms} ms (updating…)`
					: `Auto selected: ${loc.label} · ~${ms} ms`;
			}

			if (this.measuring) {
				return 'Checking latency to each location…';
			}

			return `Auto selected: ${loc.label} (default)`;
		},

		get autoReason() {
			if (this.measuring) {
				return this.autoMode === 'latency'
					? 'Sampling latency — values may still settle'
					: 'Measuring round-trip time from your connection…';
			}

			if (this.autoMode === 'latency') {
				return 'Lowest stable estimate from your connection';
			}

			return 'Default because latency could not be measured';
		},

		init() {
			this.loadLocationsFromDom();
			this.seedMeasuringState();
			this.syncOptionLabels();
			this.measureAll({ refresh: false }).then(() => {
				this.syncAutoSelection();
				this.startRefreshLoop();
			});

			this.$watch(
				() => this.$wire?.get(`itemConfigurations.${this.itemIndex}.location_id`),
				() => {
					this.syncAutoSelection();
					this.$nextTick(() => this.syncOptionLabels());
				},
			);

			this._onVisibility = () => {
				if (document.hidden) {
					this.stopRefreshLoop();
					return;
				}
				this.startRefreshLoop();
				if (!this._probeInFlight) {
					this.measureAll({ refresh: true }).then(() => this.syncAutoSelection());
				}
			};
			document.addEventListener('visibilitychange', this._onVisibility);

			// Livewire morphs can rewrite <option> text; re-apply latency labels.
			if (typeof Livewire !== 'undefined' && typeof Livewire.hook === 'function') {
				this._morphHook = Livewire.hook('morph.updated', ({ el }) => {
					if (!this.$el || !el) {
						return;
					}
					if (el === this.$el || this.$el.contains(el) || el.contains?.(this.$el)) {
						this.$nextTick(() => this.syncOptionLabels());
					}
				});
			}
		},

		destroy() {
			this.stopRefreshLoop();
			if (this._onVisibility) {
				document.removeEventListener('visibilitychange', this._onVisibility);
			}
			this._onVisibility = null;

			if (typeof this._morphHook === 'function') {
				try {
					this._morphHook();
				} catch (e) {
					// ignore
				}
			}
			this._morphHook = null;
		},

		startRefreshLoop() {
			this.stopRefreshLoop();
			if (document.hidden) {
				return;
			}

			this._refreshTimer = setInterval(() => {
				if (document.hidden || this._probeInFlight) {
					return;
				}
				this.measureAll({ refresh: true }).then(() => this.syncAutoSelection());
			}, this.refreshIntervalMs);
		},

		stopRefreshLoop() {
			if (this._refreshTimer) {
				clearInterval(this._refreshTimer);
			}
			this._refreshTimer = null;
		},

		loadLocationsFromDom() {
			const raw = this.$el?.getAttribute?.('data-locations') || '[]';
			try {
				const parsed = JSON.parse(raw);
				this.locations = Array.isArray(parsed) ? parsed : [];
			} catch (e) {
				this.locations = [];
			}
		},

		seedMeasuringState() {
			const next = {};
			for (const loc of this.locations) {
				next[loc.id] = {
					status: (loc.latency_host || '').trim() ? 'measuring' : 'n/a',
					ms: null,
					samples: [],
				};
			}
			this.results = next;
		},

		defaultLocation() {
			const nyc = this.locations.find((loc) => String(loc.tag || '').toLowerCase() === 'nyc');
			if (nyc) {
				return nyc;
			}

			return this.locations[0] || null;
		},

		median(samples) {
			if (!samples.length) {
				return null;
			}
			const sorted = [...samples].sort((a, b) => a - b);
			const mid = Math.floor(sorted.length / 2);
			return sorted.length % 2 === 0
				? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
				: sorted[mid];
		},

		setResult(id, result, { pickWinner = true } = {}) {
			this.results = {
				...this.results,
				[id]: result,
			};
			if (pickWinner) {
				this.pickAutoWinner();
			}
			this.syncOptionLabels();
			if (this.isAutoSelected) {
				this.syncAutoSelection();
			}
		},

		latencyLabel(id) {
			const result = this.results[id];
			if (!result) {
				return this.measuring ? '…' : 'n/a';
			}
			if (result.status === 'ok' && result.ms != null) {
				return this.measuring ? `~${result.ms} ms…` : `~${result.ms} ms`;
			}
			if (result.status === 'measuring') {
				return '…';
			}
			if (result.status === 'unreachable') {
				return 'unreachable';
			}

			return 'n/a';
		},

		latencyClass(id) {
			const result = this.results[id];
			if (!result || result.status !== 'ok' || result.ms == null) {
				return 'text-gray-400 dark:text-gray-500';
			}
			if (result.ms < 80) {
				return 'text-emerald-600 dark:text-emerald-400';
			}
			if (result.ms < 160) {
				return 'text-amber-600 dark:text-amber-400';
			}

			return 'text-orange-600 dark:text-orange-400';
		},

		syncOptionLabels() {
			const select = this.$refs?.locationSelect || this.$el?.querySelector?.('select');
			if (!select) {
				return;
			}

			for (const option of select.options) {
				const base = option.getAttribute('data-base-label') || option.textContent.trim();
				if (!option.getAttribute('data-base-label')) {
					option.setAttribute('data-base-label', base);
				}

				if (option.value === '' || option.value == null) {
					const auto = this.autoLocation;
					const autoMs = auto ? this.results[auto.id]?.ms : null;
					if (auto && autoMs != null) {
						option.textContent = this.measuring
							? `~${autoMs} ms… — Automatic (Best Available) — ${auto.label}`
							: `~${autoMs} ms — Automatic (Best Available) — ${auto.label}`;
					} else if (this.measuring) {
						option.textContent = '… — Automatic (Best Available)';
					} else {
						option.textContent = 'Automatic (Best Available)';
					}
					continue;
				}

				const result = this.results[option.value] || this.results[Number(option.value)];
				if (!result || result.status === 'n/a') {
					option.textContent = base;
				} else if (result.status === 'measuring') {
					option.textContent = `… — ${base}`;
				} else if (result.status === 'ok' && result.ms != null) {
					option.textContent = this.measuring
						? `~${result.ms} ms… — ${base}`
						: `~${result.ms} ms — ${base}`;
				} else if (result.status === 'unreachable') {
					option.textContent = `unreachable — ${base}`;
				} else {
					option.textContent = base;
				}
			}
		},

		async measureAll({ refresh = false } = {}) {
			if (this._probeInFlight) {
				return;
			}

			this._probeInFlight = true;

			try {
				if (!refresh) {
					this.measuring = true;
					this.seedMeasuringState();
					this.syncOptionLabels();
				}

				const sampleCount = refresh ? this.refreshSampleCount : this.initialSampleCount;
				await Promise.all(
					this.locations.map((loc) =>
						this.measureLocationProgressive(loc, {
							sampleCount,
							append: refresh,
						}),
					),
				);

				this.pickAutoWinner({ allowHysteresis: refresh });
			} finally {
				this.measuring = false;
				this._probeInFlight = false;
				this.syncOptionLabels();
			}
		},

		async measureLocationProgressive(loc, { sampleCount = 4, append = false } = {}) {
			const host = (loc.latency_host || '').trim();
			if (!host) {
				this.setResult(loc.id, { status: 'n/a', ms: null, samples: [] }, { pickWinner: false });
				return;
			}

			const port = Number(loc.latency_port) || 443;
			const scheme = port === 80 ? 'http' : 'https';
			const authority = (scheme === 'https' && port === 443) || (scheme === 'http' && port === 80)
				? host
				: `${host}:${port}`;

			let samples = append && Array.isArray(this.results[loc.id]?.samples)
				? [...this.results[loc.id].samples]
				: [];

			for (let i = 0; i < sampleCount; i++) {
				const ms = await this.probeWithImage(`${scheme}://${authority}/?latency=${Date.now()}-${i}`);
				if (ms == null) {
					continue;
				}

				samples.push(ms);
				if (samples.length > this.maxSampleWindow) {
					samples = samples.slice(-this.maxSampleWindow);
				}

				this.setResult(loc.id, {
					status: 'ok',
					ms: this.median(samples),
					samples: [...samples],
				}, { pickWinner: !append });
			}

			if (samples.length === 0) {
				this.setResult(loc.id, { status: 'unreachable', ms: null, samples: [] }, { pickWinner: !append });
			}
		},

		/**
		 * Measure RTT via Image load. CSP connect-src blocks fetch() to DC hosts,
		 * but img-src already allows https:/http:, and onload/onerror both fire after
		 * the browser completes the network round-trip (HTML responses error as images).
		 */
		probeWithImage(url) {
			return new Promise((resolve) => {
				const img = new Image();
				const started = performance.now();
				let settled = false;

				const finish = (ok) => {
					if (settled) {
						return;
					}
					settled = true;
					clearTimeout(timer);
					img.onload = null;
					img.onerror = null;
					img.src = '';
					resolve(ok ? Math.max(1, Math.round(performance.now() - started)) : null);
				};

				const timer = setTimeout(() => finish(false), 4000);
				img.onload = () => finish(true);
				img.onerror = () => finish(true);
				img.src = url;
			});
		},

		findBestLatencyCandidate() {
			let bestId = null;
			let bestMs = Number.POSITIVE_INFINITY;

			for (const loc of this.locations) {
				const result = this.results[loc.id];
				if (result?.status === 'ok' && result.ms != null && result.ms < bestMs) {
					bestMs = result.ms;
					bestId = loc.id;
				}
			}

			if (bestId == null) {
				return null;
			}

			return { id: bestId, ms: bestMs };
		},

		acceptAutoWinner(id, mode = 'latency') {
			this.autoResolvedId = id;
			this.autoMode = mode;
			this.pendingChallengerId = null;
			this.pendingChallengerRounds = 0;
		},

		pickAutoWinner({ allowHysteresis = false } = {}) {
			const best = this.findBestLatencyCandidate();

			if (!best) {
				const fallback = this.defaultLocation();
				this.acceptAutoWinner(fallback?.id ?? null, 'default');
				return;
			}

			const currentId = this.autoResolvedId != null ? Number(this.autoResolvedId) : null;
			const currentResult = currentId != null ? this.results[currentId] : null;
			const currentMs = currentResult?.status === 'ok' ? currentResult.ms : null;

			// First latency lock, or current pick became unusable: accept immediately.
			if (
				!allowHysteresis
				|| this.autoMode !== 'latency'
				|| currentId == null
				|| currentMs == null
				|| Number(best.id) === currentId
			) {
				this.acceptAutoWinner(best.id, 'latency');
				return;
			}

			// Only switch Auto when a challenger beats the current pick by a clear margin
			// across consecutive refresh rounds — avoids thrash from probe noise.
			if (best.ms <= currentMs - this.hysteresisMs) {
				if (Number(this.pendingChallengerId) === Number(best.id)) {
					this.pendingChallengerRounds += 1;
				} else {
					this.pendingChallengerId = best.id;
					this.pendingChallengerRounds = 1;
				}

				if (this.pendingChallengerRounds >= this.hysteresisRounds) {
					this.acceptAutoWinner(best.id, 'latency');
				}

				return;
			}

			this.pendingChallengerId = null;
			this.pendingChallengerRounds = 0;
		},

		syncAutoSelection() {
			if (!this.isAutoSelected) {
				return;
			}

			const id = this.autoResolvedId != null ? Number(this.autoResolvedId) : null;
			if (id === this.syncedId) {
				return;
			}

			this.syncedId = id;
			this.$wire?.call('setAutoResolvedLocationId', this.itemIndex, id);
		},
	});

	Alpine.data('locationLatencyPicker', (itemIndex) => window.locationLatencyPicker(itemIndex));

	const copyTextToClipboard = async (text) => {
		if (window.isSecureContext && navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(text);

			return;
		}

		const input = document.createElement('textarea');
		input.value = text;
		input.setAttribute('readonly', '');
		input.style.position = 'fixed';
		input.style.left = '-9999px';
		document.body.appendChild(input);
		input.select();
		const copied = document.execCommand('copy');
		input.remove();

		if (! copied) {
			throw new Error('copy failed');
		}
	};

	const applyReferralCopyStatus = (button, status) => {
		button.querySelectorAll('[data-copy-status]').forEach((el) => {
			el.hidden = el.getAttribute('data-copy-status') !== status;
		});
	};

	const announceReferralCopy = (style, message) => {
		window.dispatchEvent(new CustomEvent('banner-message', {
			bubbles: true,
			detail: { style, message },
		}));
	};

	Alpine.data('ppaScrollGate', () => ({
		reachedEnd: false,

		init() {
			this.$nextTick(() => this.checkFit());
		},

		checkFit() {
			const el = this.$refs.ppaBody;
			if (! el) {
				return;
			}
			const fitsWithoutScroll = el.scrollHeight <= el.clientHeight + 1;
			const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
			this.reachedEnd = fitsWithoutScroll || atEnd;
		},
	}));

	Alpine.data('partnerSetupStripeReturn', (stripe) => ({
		stripe: stripe || null,

		init() {
			this.focusPayouts();
		},

		focusPayouts() {
			if (this.stripe !== 'return' && this.stripe !== 'refresh') {
				return;
			}

			scrollToPartnerHashTarget('step-payouts');
		},
	}));

	Alpine.data('partnerReferralCopy', () => ({
		url: '',
		status: '',

		init() {
			this.url = this.$el.getAttribute('data-partner-referral-url') || '';
		},

		async copyLink() {
			try {
				await copyTextToClipboard(this.url);
				this.status = 'copied';
				applyReferralCopyStatus(this.$el, 'copied');
				this.$dispatch('banner-message', { style: 'success', message: 'Referral link copied!' });
			} catch (error) {
				this.status = 'failed';
				applyReferralCopyStatus(this.$el, 'failed');
				this.$dispatch('banner-message', { style: 'danger', message: 'Could not copy the referral link. Copy it from the text above.' });
			}
		},
	}));

	if (! window.__attoricPartnerReferralCopyBound) {
		window.__attoricPartnerReferralCopyBound = true;
		document.addEventListener('click', async (event) => {
			const button = event.target.closest('[data-partner-referral-copy]');
			if (! button) {
				return;
			}

			event.preventDefault();
			const url = button.getAttribute('data-partner-referral-url') || '';

			try {
				await copyTextToClipboard(url);
				applyReferralCopyStatus(button, 'copied');
				announceReferralCopy('success', 'Referral link copied!');
			} catch (error) {
				applyReferralCopyStatus(button, 'failed');
				announceReferralCopy('danger', 'Could not copy the referral link. Copy it from the text above.');
			}
		});
	}

	Alpine.data('bannerMessage', (style, message) => ({
		show: true,
		style,
		message,

		handleBannerMessage(event) {
			if (!event?.detail) {
				return;
			}

			this.style = event.detail.style;
			this.message = event.detail.message;
			this.show = true;
		},
	}));

	Alpine.data('actionMessage', () => ({
		shown: false,
		timeout: null,

		trigger() {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			this.shown = true;
			this.timeout = setTimeout(() => {
				this.shown = false;
			}, 2000);
		},
	}));

	Alpine.data('cartToast', () => ({
		show: false,
		planName: '',
		timeout: null,
		scrolledPast: false,
		scrollThreshold: 400,
		_onScroll: null,

		init() {
			this._onScroll = () => {
				this.scrolledPast = window.scrollY > this.scrollThreshold;
			};
			this._onScroll();
			window.addEventListener('scroll', this._onScroll, { passive: true });
		},

		handleCartToast(event) {
			const detail = event?.detail;
			const fromArray = Array.isArray(detail) ? detail[0] : null;
			this.planName =
				(typeof detail?.planName === 'string' && detail.planName) ||
				(typeof fromArray?.planName === 'string' && fromArray.planName) ||
				'Server';
			this.show = true;
			if (this.timeout) {
				clearTimeout(this.timeout);
			}
			this.timeout = setTimeout(() => {
				this.show = false;
			}, 5000);
		},

		dismiss() {
			this.show = false;
			if (this.timeout) {
				clearTimeout(this.timeout);
			}
		},

		scrollToTop() {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},

		destroy() {
			if (this.timeout) {
				clearTimeout(this.timeout);
			}
			if (this._onScroll) {
				window.removeEventListener('scroll', this._onScroll);
			}
		},
	}));

	Alpine.data('delayedFocus', (refName, delay = 250) => ({
		refName,
		delay,

		focus() {
			setTimeout(() => {
				const element = this.$refs?.[this.refName];
				if (element && typeof element.focus === 'function') {
					element.focus();
				}
			}, this.delay);
		},
	}));

	Alpine.data('delayedSelect', (refName, delay = 250) => ({
		refName,
		delay,

		select() {
			setTimeout(() => {
				const element = this.$refs?.[this.refName];
				if (element && typeof element.select === 'function') {
					element.select();
				}
			}, this.delay);
		},
	}));

	Alpine.data('passwordConfirmation', (confirmableId) => ({
		confirmableId,
		timeout: null,

		handlePasswordConfirmed(event) {
			if (!event?.detail || event.detail.id !== this.confirmableId) {
				return;
			}

			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			this.timeout = setTimeout(() => {
				const span = this.$refs?.span;
				if (!span) {
					return;
				}

				span.dispatchEvent(new CustomEvent('then', { bubbles: false }));
			}, 250);
		},
	}));

	Alpine.data('profilePhotoUploader', () => ({
		photoName: null,
		photoPreview: null,

		updatePreview() {
			const input = this.$refs?.photo;
			const file = input?.files?.[0];

			if (!file) {
				return;
			}

			this.photoName = file.name;

			const reader = new FileReader();
			reader.onload = (event) => {
				this.photoPreview = event?.target?.result ?? null;
			};
			reader.readAsDataURL(file);
		},
	}));

	Alpine.data('twoFactorToggle', () => ({
		recovery: false,

		showRecovery() {
			this.recovery = true;
			this.$nextTick(() => {
				if (this.$refs?.recovery_code) {
					this.$refs.recovery_code.focus();
				}
			});
		},

		showCode() {
			this.recovery = false;
			this.$nextTick(() => {
				if (this.$refs?.code) {
					this.$refs.code.focus();
				}
			});
		},
	}));

	Alpine.data('cartDropdown', () => ({
		open: false,
		toggle() {
			this.open = !this.open;
		},
		close() {
			this.open = false;
		},
		openCart() {
			this.open = true;
		},
	}));

	Alpine.data('partnerReferralCode', () => ({
		showPartnerField: false,
		showTrigger: true,
		toggle() {
			this.showPartnerField = !this.showPartnerField;
			this.showTrigger = !this.showPartnerField;
		},
	}));

	Alpine.data('billingStateCounter', () => ({
		stateLen: 0,
		init() {
			const value = this.$wire.get('billingState');
			this.stateLen = value ? String(value).length : 0;
		},
		syncFromInput(event) {
			this.stateLen = event.target.value.length;
		},
	}));

	Alpine.data('livewireUserTimezone', () => ({
		timezone: '',
		init() {
			this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			this.$wire.set('userTimezone', this.timezone);
		},
	}));

	Alpine.data('focusOnMount', () => ({
		init() {
			this.$nextTick(() => {
				this.$el.focus();
				this.$el.scrollIntoView({ block: 'nearest' });
			});
		},
	}));

	Alpine.data('nextBillingDateModal', () => ({
		init() {
			this.$nextTick(() => this.focusDateInput());
		},
		focusDateInput() {
			const root = this.$refs.dialogRoot;
			if (! root) {
				return;
			}
			const input = root.querySelector('input[type=date]');
			if (input) {
				input.focus();
			}
		},
		close() {
			this.$wire.set('showNextBillingDateModal', false);
		},
	}));

	Alpine.data('highlightTicketReply', () => ({
		init() {
			this.$nextTick(() => this.flash());
		},
		flash() {
			this.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			setTimeout(() => this.clearHighlight(), 3000);
		},
		clearHighlight() {
			this.$el.classList.remove('ring-2', 'ring-yellow-400', 'dark:ring-yellow-500');
		},
	}));

	Alpine.data('progressBar', (initialValue = 0) => ({
		value: initialValue,

		init() {
			this.updateProgress();
		},

		updateProgress() {
			this.$el.style.setProperty('--progress', this.value + '%');
		},

		setValue(newValue) {
			this.value = newValue;
			this.updateProgress();
		},
	}));

	Alpine.data('ampSyncCooldown', (initialRemaining = 0) => ({
		remaining: Number(initialRemaining) || 0,
		timer: null,

		init() {
			this.startTimer();
		},

		startTimer() {
			if (this.timer) {
				clearInterval(this.timer);
				this.timer = null;
			}

			if (this.remaining <= 0) {
				return;
			}

			this.timer = setInterval(() => {
				this.remaining = Math.max(0, this.remaining - 1);
				if (this.remaining === 0 && this.timer) {
					clearInterval(this.timer);
					this.timer = null;
				}
			}, 1000);
		},

		destroy() {
			if (this.timer) {
				clearInterval(this.timer);
			}
		},
	}));

	Alpine.data('photoPreview', () => ({
		photoName: null,
		photoPreview: null,
		hasPreview: false,

		init() {
			// Watch for changes to update preview
		},

		updatePreview() {
			const input = this.$refs?.photo;
			const file = input?.files?.[0];

			if (!file) {
				return;
			}

			this.photoName = file.name;

			const reader = new FileReader();
			reader.onload = (event) => {
				this.photoPreview = event?.target?.result ?? null;
				this.hasPreview = !!this.photoPreview;
			};
			reader.readAsDataURL(file);
		},

		clearPreview() {
			this.photoPreview = null;
			this.photoName = null;
			this.hasPreview = false;
		},
	}));

	Alpine.data('cookieConsent', (gaId, gaDebug) => window.cookieConsent(gaId, gaDebug));
	Alpine.data('discordLinkPrompt', window.discordLinkPrompt);
	Alpine.data('paymentMethodForm', (clientSecret, stripeKey) => window.paymentMethodForm(
		clientSecret,
		stripeKey,
	));
	Alpine.data('invoicePaymentManager', (
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		invoicesListUrl,
	) => window.invoicePaymentManager({
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		invoicesListUrl,
	}));
	Alpine.data('checkoutPayment', (
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		initialPaymentMethod,
		stripeSetupClientSecret,
		isSetupMode,
	) => window.checkoutPayment({
		stripeClientSecret,
		customerSessionClientSecret,
		stripeKey,
		initialPaymentMethod,
		stripeSetupClientSecret,
		isSetupMode,
	}));
	Alpine.data('ampWebSocketManager', (webSocketEndpoint) => window.ampWebSocketManager(webSocketEndpoint));
	Alpine.data('gameGallery', (totalGames, maxIndex) => window.gameGallery(totalGames, maxIndex));
	Alpine.data('ticketDetailModals', () => ({
		showRating: false,
		showReopen: false,
		showResolve: false,
		showClose: false,
	}));

	// Drives direct browser-to-B2 multipart uploads for ticket attachments.
	// Positional scalar args only — CSP-safe Alpine's expression parser is
	// flaky with inline object literals, and if x-data fails to parse Alpine
	// silently drops every other x-* directive on the element (including the
	// submit/beforeunload guards below), causing the <form> to fall through
	// to a native GET submit. Keep the signature scalars-only.
	//
	// Arguments:
	//   livewireProperty: kept for backward-compat / debugging. The component
	//     no longer mutates the Livewire property directly — the property is
	//     #[Locked] on the server — but we keep the arg so the Blade
	//     template still tells us which property the action pair belongs to.
	//   actionName: Livewire method to call on submit (e.g. 'submit', 'sendReply').
	//   maxFileSize: per-file byte cap surfaced to Uppy's restrictions.
	// The Dashboard renders inline into x-ref="dashboard" on the host div.
	Alpine.data('ticketAttachmentUppy', (livewireProperty, actionName, maxFileSize) => ({
		livewireProperty,
		addMethod: 'addB2Upload',
		removeMethod: 'removeB2UploadByKey',
		maxFileSize: maxFileSize || 20 * 1024 * 1024 * 1024,
		// Kept in sync with the server-side allowlist in
		// App\Services\TicketAttachmentFilenameGuard::DISALLOWED_UPLOAD_EXTENSIONS.
		// The server has final say (a tampered client can't bypass it), but
		// matching here gives the user a fast inline rejection via uppy.info()
		// instead of a round-trip after the upload starts.
		disallowedExtensions: [
			'php', 'phtml', 'phar', 'hta',
		],
		completed: [],
		uploading: false,
		pendingSubmit: false,
		// The Uppy Dashboard lives inside a collapsible region so the form
		// stays compact until the user actually wants to attach something.
		// file-added flips this to true; a successful submit resets it.
		dashboardOpen: false,
		// Reactive copy of whether the reply textarea (when present) is empty,
		// so the submit button can be disabled client-side without a
		// round-trip. Updated via an 'input' listener on the textarea.
		replyEmpty: true,
		action: actionName || 'submit',
		_onSubmit: null,
		_onBeforeUnload: null,
		_onReplyInput: null,
		_onReplyKeydown: null,
		_replyTextarea: null,
		_offReplySent: null,
		_suppressWireSync: false,

		init() {
			// Bind the submit + beforeunload guards FIRST, before any Uppy setup,
			// so the guards still work if Uppy initialization throws for any reason.
			// Using native addEventListener avoids CSP-safe Alpine expression parsing.
			this._onSubmit = (event) => {
				event.preventDefault();
				this.queueSubmit();
			};
			this._onBeforeUnload = (event) => {
				if (this.uploading || this.pendingSubmit) {
					event.preventDefault();
					event.returnValue = '';
				}
			};
			if (this.$root && typeof this.$root.addEventListener === 'function') {
				this.$root.addEventListener('submit', this._onSubmit);
			}
			window.addEventListener('beforeunload', this._onBeforeUnload);

			// Reply forms have a required textarea; mirror its emptiness into
			// Alpine state so the submit button can disable itself immediately
			// (wire:model without .live wouldn't sync per-keystroke otherwise).
			if (this.action === 'sendReply' && this.$root && typeof this.$root.querySelector === 'function') {
				const textarea = this.$root.querySelector('textarea[wire\\:model="replyContent"]');
				if (textarea) {
					this._replyTextarea = textarea;
					this.replyEmpty = String(textarea.value || '').trim() === '';
					this._onReplyInput = () => {
						this.replyEmpty = String(textarea.value || '').trim() === '';
					};
					this._onReplyKeydown = (e) => {
						if (e.key !== 'Enter' || e.shiftKey || e.isComposing) {
							return;
						}
						// Do not send while a modal or similar has focus; respect native IME.
						if (e.defaultPrevented) {
							return;
						}
						e.preventDefault();
						this.queueSubmit();
					};
					textarea.addEventListener('input', this._onReplyInput);
					textarea.addEventListener('keydown', this._onReplyKeydown);
				}
			}

			// Livewire dispatches 'reply-sent' from the server after a
			// successful reply commit. Listening here runs on the LIVE
			// component post-morph, which is more reliable than $wire method
			// return Promises (Livewire may replace the Alpine proxy during
			// morph, making `this` inside a .then() a stale reference).
			if (this.$wire && typeof this.$wire.on === 'function') {
				const handler = () => {
					this.$nextTick(() => this.cleanupAfterSubmit());
				};
				this._offReplySent = this.$wire.on('reply-sent', handler);
			}

			const csrfEl = document.querySelector('meta[name="csrf-token"]');
			const csrf = csrfEl ? csrfEl.getAttribute('content') : '';

			const jsonFetch = (url, body, method) =>
				fetch(url, {
					method: method || 'POST',
					credentials: 'same-origin',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						'X-CSRF-TOKEN': csrf,
						'X-Requested-With': 'XMLHttpRequest',
					},
					body: body ? JSON.stringify(body) : undefined,
				}).then(async (response) => {
					if (!response.ok) {
						let message = 'Upload request failed (' + response.status + ')';
						try {
							const data = await response.json();
							if (data && data.message) {
								message = data.message;
							}
						} catch (e) { /* ignore */ }
						throw new Error(message);
					}
					return response.json();
				});

			const disallowed = this.disallowedExtensions;
			const hasDisallowed = (name) => {
				if (!name) {
					return null;
				}
				const segments = String(name).split('.');
				for (let i = 0; i < segments.length; i++) {
					const seg = segments[i].trim().toLowerCase();
					if (seg && disallowed.indexOf(seg) !== -1) {
						return seg;
					}
				}
				return null;
			};

			const uppy = new Uppy({
				autoProceed: true,
				allowMultipleUploadBatches: true,
				restrictions: {
					maxFileSize: this.maxFileSize,
					// Mirrors the server-side MAX_PENDING_B2_UPLOADS cap on
					// the Livewire components (CreateTicket / TicketDetail /
					// SupportTicketDetail). Keep the two in lockstep.
					maxNumberOfFiles: 50,
				},
				onBeforeFileAdded: (file) => {
					const bad = hasDisallowed(file.name);
					if (bad) {
						uppy.info('Files with the .' + bad + ' extension are not allowed.', 'error', 5000);
						return false;
					}
					return true;
				},
			})
				.use(Dashboard, {
					target: this.$refs.dashboard,
					inline: true,
					height: 280,
					width: '100%',
					proudlyDisplayPoweredByUppy: false,
					showProgressDetails: true,
					note: 'Files upload directly to encrypted storage. Keep this tab open until all uploads finish.',
					theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
				})
				.use(AwsS3, {
					shouldUseMultipart: true,
					createMultipartUpload: (file) =>
						jsonFetch('/uploads/b2/create-multipart', {
							filename: file.name,
							size: file.size,
							content_type: file.type || 'application/octet-stream',
						}),
					signPart: (file, partData) => {
						const params = new URLSearchParams({
							key: partData.key,
							uploadId: partData.uploadId,
							partNumber: String(partData.partNumber),
						});
						return jsonFetch('/uploads/b2/sign-part?' + params.toString(), null, 'GET');
					},
					listParts: (file, partData) => {
						const params = new URLSearchParams({
							key: partData.key,
							uploadId: partData.uploadId,
						});
						return jsonFetch('/uploads/b2/list-parts?' + params.toString(), null, 'GET');
					},
					completeMultipartUpload: (file, partData) =>
						jsonFetch('/uploads/b2/complete-multipart', {
							key: partData.key,
							uploadId: partData.uploadId,
							parts: partData.parts,
						}),
					abortMultipartUpload: (file, partData) =>
						jsonFetch('/uploads/b2/abort-multipart', {
							key: partData.key,
							uploadId: partData.uploadId,
						}, 'DELETE'),
				});

			uppy.on('file-added', (file) => {
				// Flip uploading=true the moment a file enters Uppy so the
				// beforeunload / deferred-submit guards cover the presign gap
				// (Uppy's own 'upload' event doesn't fire until transfer starts).
				this.uploading = true;
				// Keep the dashboard visible once anything has been added, in
				// case a file was dropped/added while the compact trigger was
				// showing (e.g. drag-drop from a second form tab).
				this.dashboardOpen = true;
				// Stamp a stable per-file client id so we can correlate
				// completed/removed events without relying on name+size,
				// which collides when a user attaches two identical files.
				if (file) {
					file.meta = file.meta || {};
					if (!file.meta.clientId) {
						file.meta.clientId = (file.id ? String(file.id) : '')
							|| ('f-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10));
					}
				}
			});
			uppy.on('upload', () => {
				this.uploading = true;
			});
			uppy.on('upload-success', (file, response) => {
				const key = response && response.body && response.body.key;
				if (!key) {
					return;
				}
				file.meta = file.meta || {};
				file.meta.b2Key = key;
				// Carry the per-file client id onto the completed entry so
				// file-removed can find the right row even if the user has
				// two attachments with identical name+size.
				const entry = {
					key: key,
					client_id: file.meta.clientId || null,
					original_name: file.name,
					mime_type: file.type || 'application/octet-stream',
					size: file.size,
				};
				this.completed.push(entry);
				this.appendToWire(entry);
			});
			uppy.on('complete', () => {
				this.uploading = false;
				if (this.pendingSubmit) {
					this.pendingSubmit = false;
					this.$nextTick(() => this.runSubmit());
				}
			});
			uppy.on('file-removed', (file) => {
				const removedKey = (file && file.meta && file.meta.b2Key) ? file.meta.b2Key : null;
				if (removedKey) {
					// B2 key is globally unique once assigned; safest match.
					this.completed = this.completed.filter((entry) => entry.key !== removedKey);
					this.removeFromWire(removedKey);
					return;
				}
				// File was removed before its upload completed (no b2Key yet).
				// Prefer the stable per-file clientId over name+size so we
				// can't accidentally nuke a sibling duplicate that does have
				// a b2Key assigned.
				const clientId = (file && file.meta && file.meta.clientId) ? file.meta.clientId : null;
				if (clientId) {
					this.completed = this.completed.filter((entry) => entry.client_id !== clientId);
					return;
				}
				// Last-resort fallback: shouldn't hit this since file-added
				// always stamps a clientId, but keep a safe no-op so we
				// don't silently wipe unrelated rows on name+size collisions.
			});
			uppy.on('cancel-all', () => {
				const keys = this.completed.map((entry) => entry.key);
				this.completed = [];
				this.uploading = false;
				this.pendingSubmit = false;
				keys.forEach((key) => this.removeFromWire(key));
			});

			uppyByComponent.set(this, uppy);
		},

		appendToWire(entry) {
			if (!this.$wire || !entry || !entry.key) {
				return;
			}
			try {
				// Fire and forget: we don't need to block the UI on the
				// server's response. The server-side property is #[Locked]
				// and validates every appended entry.
				this.$wire.call(this.addMethod, entry);
			} catch (e) {
				console.warn('ticketAttachmentUppy: failed to append upload to Livewire', e);
			}
		},

		removeFromWire(key) {
			if (!this.$wire || !key) {
				return;
			}
			// The server-side pending array was already cleared by the
			// submit action that just succeeded; no need to chat with the
			// server for each file we're about to wipe from Uppy locally.
			if (this._suppressWireSync) {
				return;
			}
			try {
				this.$wire.call(this.removeMethod, key);
			} catch (e) {
				console.warn('ticketAttachmentUppy: failed to remove upload from Livewire', e);
			}
		},

		isUploading() {
			return this.uploading;
		},

		openDashboard() {
			this.dashboardOpen = true;
		},

		progressLabel(prefix) {
			// Uppy's own dashboard displays per-file % progress; the submit
			// button label only needs a generic "still uploading" hint.
			return prefix;
		},

		queueSubmit() {
			if (this.pendingSubmit) {
				return;
			}
			// The submit button is disabled when the reply textarea is empty,
			// but drag-drop on a completed upload (or a stale click) could
			// still reach here. Fire $wire immediately so Livewire renders
			// the required-field error inline without queuing a post-upload
			// auto-fire that would waste a completed upload.
			if (this.action === 'sendReply' && this.replyEmpty) {
				this.runSubmit();
				return;
			}
			if (this.uploading) {
				this.pendingSubmit = true;
				const uppy = uppyByComponent.get(this);
				if (uppy && typeof uppy.info === 'function') {
					uppy.info(
						"Uploads still finishing — your message will send when they're done.",
						'info',
						4000,
					);
				}
				return;
			}
			this.runSubmit();
		},

		runSubmit() {
			try {
				this.$wire[this.action]();
			} catch (e) {
				console.warn('ticketAttachmentUppy: submit action threw', e);
			}
			// Cleanup now runs via the 'reply-sent' server dispatch listener,
			// which is more reliable than the Promise returned by $wire. For
			// create-ticket (action='submit') the server redirects on success
			// so client-side cleanup isn't needed; on validation failure the
			// dashboard should stay open so the user can fix and retry.
		},

		cleanupAfterSubmit() {
			// Clear Uppy's file list so the dashboard doesn't keep showing
			// files that have already been finalized into the ticket. Suppress
			// per-file removeFromWire calls — the server already reset the
			// pending array in its submit handler.
			const uppy = uppyByComponent.get(this);
			if (uppy) {
				this._suppressWireSync = true;
				try {
					uppy.cancelAll();
				} catch (e) { /* ignore */ }
				this._suppressWireSync = false;
			}
			this.completed = [];
			this.pendingSubmit = false;
			this.uploading = false;
			// Collapse back to the compact trigger so the next reply starts
			// with a small form again.
			this.dashboardOpen = false;
			// Server-side $this->reset(['replyContent']) just wiped the
			// textarea via morph; the 'input' event doesn't fire for
			// programmatic resets, so re-read the DOM to sync our state.
			if (this._replyTextarea) {
				this.replyEmpty = String(this._replyTextarea.value || '').trim() === '';
			}
		},

		destroy() {
			if (this._onSubmit && this.$root && typeof this.$root.removeEventListener === 'function') {
				this.$root.removeEventListener('submit', this._onSubmit);
			}
			if (this._onBeforeUnload) {
				window.removeEventListener('beforeunload', this._onBeforeUnload);
			}
			if (this._onReplyInput && this._replyTextarea) {
				try {
					this._replyTextarea.removeEventListener('input', this._onReplyInput);
				} catch (e) { /* ignore */ }
			}
			if (this._onReplyKeydown && this._replyTextarea) {
				try {
					this._replyTextarea.removeEventListener('keydown', this._onReplyKeydown);
				} catch (e) { /* ignore */ }
			}
			if (typeof this._offReplySent === 'function') {
				try { this._offReplySent(); } catch (e) { /* ignore */ }
			}
			this._onSubmit = null;
			this._onBeforeUnload = null;
			this._onReplyInput = null;
			this._onReplyKeydown = null;
			this._replyTextarea = null;
			this._offReplySent = null;
			const uppy = uppyByComponent.get(this);
			if (uppy) {
				try {
					uppy.destroy();
				} catch (e) { /* ignore */ }
				uppyByComponent.delete(this);
			}
		},
	}));

	window.creditsPurchase = (clientSecret, paymentMethod, stripeKey, paypalClientId) => ({
		stripe: null,
		elements: null,
		cardElement: null,
		isProcessing: false,
		clientSecret,
		paymentMethod,
		paypalButtonsInstance: null,
		paypalInitialized: false,
		paypalBlocked: false,
		stripeBlocked: false,
		stripeInitialized: false,
		paypalClientId,
		stripeKey,

		shouldCollectPayment() {
			return true;
		},

		getActiveClientSecret() {
			return this.clientSecret;
		},

		mountPaymentSdks(options = {}) {
			return mountCheckoutPaymentSdks(this, options, {
				ensureSdks: async (component) => {
					await ensureStripeSdk().catch((error) => {
						component.stripeBlocked = true;
						throw error;
					});

					const clientId = component.paypalClientId || resolvePayPalClientId(component);
					if (!clientId) {
						return;
					}

					const container = document.getElementById('paypal-credit-button');
					const currency = container?.getAttribute?.('data-paypal-currency') || null;
					await ensurePayPalSdk({ clientId, currency }).catch((error) => {
						component.paypalBlocked = true;
						throw error;
					});
				},
			});
		},

		destroyStripe() {
			if (this.cardElement) {
				try {
					this.cardElement.destroy();
				} catch (e) {
					// ignore
				}
				this.cardElement = null;
			}
			this.elements = null;
			this.stripeInitialized = false;
			const cardContainer = document.getElementById('credit-card-element');
			if (cardContainer) {
				cardContainer.innerHTML = '';
			}
		},

		init() {
			this.$nextTick(() => {
				this.mountPaymentSdks();
			});

			this._onCreditsLivewireNavigated = () => {
				this.$nextTick(() => {
					this.mountPaymentSdks({ forceRemount: true });
				});
			};
			document.addEventListener('livewire:navigated', this._onCreditsLivewireNavigated);

			this.$watch('paymentMethod', (value) => {
				if (this.$wire) {
					this.$wire.set('paymentMethod', value, false);
				}
				if (value === 'stripe') {
					this.$nextTick(() => this.initStripe());
					return;
				}

				if (value === 'paypal') {
					this.$nextTick(() => this.initPayPal());
				}
			});
		},

		destroy() {
			if (this._onCreditsLivewireNavigated) {
				document.removeEventListener('livewire:navigated', this._onCreditsLivewireNavigated);
				this._onCreditsLivewireNavigated = null;
			}
			teardownCheckoutPaymentWidgets(this);
		},

		initStripe() {
			if (typeof Stripe === 'undefined') {
				return ensureStripeSdk().then(() => this.initStripe()).catch(() => {
					this.stripeBlocked = true;
					console.error('Stripe.js not loaded');
				});
			}

			const cardContainer = document.getElementById('credit-card-element');
			if (!cardContainer || !this.stripeKey) {
				return Promise.resolve();
			}

			if (this.stripeInitialized && this.cardElement) {
				return Promise.resolve();
			}

			if (this.cardElement) {
				try {
					this.cardElement.destroy();
				} catch (e) {
					// ignore
				}
				this.cardElement = null;
			}

			this.stripe = Stripe(this.stripeKey);

			const isDarkMode =
				document.documentElement.classList.contains('dark') ||
				document.body.classList.contains('dark') ||
				(window.matchMedia('(prefers-color-scheme: dark)').matches &&
					!document.documentElement.classList.contains('light'));

			const containerBg = window.getComputedStyle(cardContainer).backgroundColor;
			const isLightBg = containerBg && (containerBg === 'rgb(255, 255, 255)' || containerBg === 'white');
			const textColor = isLightBg || !isDarkMode ? '#32325d' : '#ffffff';

			this.elements = this.stripe.elements();

			const style = {
				base: {
					color: textColor,
					fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
					fontSmoothing: 'antialiased',
					fontSize: '16px',
					'::placeholder': { color: '#9ca3af' },
				},
				invalid: { color: '#ef4444', iconColor: '#ef4444' },
			};

			this.cardElement = this.elements.create('card', { style });
			this.cardElement.mount('#credit-card-element');
			this.stripeInitialized = true;

			this.cardElement.on('change', (event) => {
				const displayError = document.getElementById('credit-card-errors');
				if (displayError) {
					displayError.textContent = event.error ? event.error.message : '';
				}
			});
			return Promise.resolve();
		},

		initPayPal() {
			const clientId = this.paypalClientId || resolvePayPalClientId(this);
			if (!clientId) {
				return Promise.resolve();
			}

			const container = document.getElementById('paypal-credit-button');
			if (!container) {
				return Promise.resolve();
			}

			if (typeof paypal === 'undefined') {
				return ensurePayPalSdk({
					clientId,
					currency: container.getAttribute('data-paypal-currency') || null,
				}).then(() => this.initPayPal()).catch(() => {
					this.paypalBlocked = true;
					console.error('PayPal SDK not loaded or container not found');
				});
			}

			if (this.paypalInitialized && this.paypalButtonsInstance) {
				return Promise.resolve();
			}

			if (this.paypalButtonsInstance && typeof this.paypalButtonsInstance.close === 'function') {
				try {
					this.paypalButtonsInstance.close();
				} catch (e) {
					// ignore
				}
			}

			container.innerHTML = '';
			const self = this;

			this.paypalButtonsInstance = paypal.Buttons({
				style: {
					shape: 'rect',
					color: 'blue',
					layout: 'vertical',
					label: 'pay',
				},

				createOrder: async () => {
					try {
						return await self.$wire.createPayPalCreditOrder();
					} catch (error) {
						console.error('Error creating PayPal order:', error);
						self.$wire.reportCreditPurchaseClientFailure({
							message: error?.message || 'Failed to create PayPal order',
							gateway: 'paypal',
							code: 'create_order_failed',
							flow: 'credits_modal_paypal',
						});
						self.$wire.dispatch('banner-message', {
							style: 'danger',
							message: `Failed to create PayPal order: ${error.message}`,
						});
						throw error;
					}
				},

				onApprove: async (data) => {
					try {
						self.isProcessing = true;
						await self.$wire.capturePayPalCreditOrder(data.orderID);
					} catch (error) {
						console.error('Error capturing PayPal order:', error);
						self.$wire.reportCreditPurchaseClientFailure({
							message: error?.message || 'Failed to complete payment',
							gateway: 'paypal',
							paypalOrderId: data?.orderID ?? null,
							code: 'capture_failed',
							flow: 'credits_modal_paypal',
						});
						self.$wire.dispatch('banner-message', {
							style: 'danger',
							message: `Failed to complete payment: ${error.message}`,
						});
					} finally {
						self.isProcessing = false;
					}
				},

				onCancel: () => {
					self.$wire.dispatch('banner-message', { style: 'warning', message: 'PayPal payment was cancelled.' });
				},

				onError: (err) => {
					console.error('PayPal error:', err);
					self.$wire.reportCreditPurchaseClientFailure(livewirePayPalFailurePayload(err, 'credits_modal_paypal'));
					self.$wire.dispatch('banner-message', { style: 'danger', message: 'An error occurred with PayPal.' });
				},
			});

			return this.paypalButtonsInstance
				.render('#paypal-credit-button')
				.then(() => {
					this.paypalInitialized = true;
				})
				.catch((err) => {
					console.error('PayPal render error:', err);
					this.paypalInitialized = false;
					this.paypalButtonsInstance = null;
				});
		},

		async submitPayment() {
			if (this.isProcessing || !this.stripe || !this.cardElement) {
				return;
			}

			this.isProcessing = true;

			try {
				const { paymentIntent, error } = await this.stripe.confirmCardPayment(this.clientSecret, {
					payment_method: { card: this.cardElement },
				});

				if (error) {
					const displayError = document.getElementById('credit-card-errors');
					if (displayError) {
						displayError.textContent = error.message;
					}
					this.$wire.reportCreditPurchaseClientFailure(livewireStripeFailurePayload(error, 'credits_modal_card'));
				} else if (paymentIntent?.status === 'succeeded') {
					this.$wire.creditsPurchased(paymentIntent.id);
				}
			} catch (error) {
				const displayError = document.getElementById('credit-card-errors');
				if (displayError) {
					displayError.textContent = 'An unexpected error occurred.';
				}
				this.$wire.reportCreditPurchaseClientFailure({
					message: error?.message || 'An unexpected error occurred.',
					gateway: 'stripe',
					code: 'js_exception',
					flow: 'credits_modal_card',
				});
			}

			this.isProcessing = false;
		},
	});

	Alpine.data('creditsPurchase', (clientSecret, paymentMethod, stripeKey, paypalClientId) => window.creditsPurchase(
		clientSecret,
		paymentMethod,
		stripeKey,
		paypalClientId,
	));
};

const reinitializeAlpineComponents = () => {
	if (!window.Alpine || !document.body) {
		return;
	}

	window.Alpine.initTree(document.body);
};

const notifyPaymentAlpineReady = () => {
	document.dispatchEvent(new CustomEvent('payment-alpine-ready'));
};

const bootPaymentAlpine = () => {
	try {
		registerAlpineComponents();
		// Re-apply after alpine-csp-boot stubs — safe to call every boot.
		registerPaymentAlpineData();
	} catch (error) {
		console.warn('Payment Alpine factories failed to register', error);
	}

	notifyPaymentAlpineReady();
	registerLivewireBillingHydrate();
	watchBillingDetailsHosts();
	hydrateBillingDetailsFromDom();
};

document.addEventListener('alpine:init', bootPaymentAlpine);
document.addEventListener('alpine:initialized', bootPaymentAlpine);

window.paymentMethodForm = (clientSecret, stripeKey, billingDetails = {}) => ({
	billingDetails,
	payerName: billingDetails.name ?? '',
	enteredPostalCode: null,
	stripe: null,
	elements: null,
	cardElement: null,
	isProcessing: false,
	clientSecret,
	stripeKey,

	init() {
		applyBillingDetailsFromDom(this);
		const displayError = document.getElementById('card-errors');

		if (typeof Stripe === 'undefined') {
			console.error('Stripe.js not loaded');
			if (displayError) {
				displayError.textContent = 'Payment form could not load. Refresh the page and try again.';
			}
			return;
		}

		if (!this.stripeKey) {
			console.error('Stripe publishable key missing');
			if (displayError) {
				displayError.textContent = 'Stripe is not configured.';
			}
			return;
		}

		this.stripe = Stripe(this.stripeKey);
		this.elements = this.stripe.elements();

		const style = {
			base: {
				color: document.documentElement.classList.contains('dark') ? '#fff' : '#32325d',
				fontFamily: '"Inter", sans-serif',
				fontSmoothing: 'antialiased',
				fontSize: '16px',
				'::placeholder': {
					color: '#aab7c4',
				},
			},
			invalid: {
				color: '#fa755a',
				iconColor: '#fa755a',
			},
		};

		this.cardElement = this.elements.create('card', { style });
		this.cardElement.mount('#card-element');

		this.cardElement.on('change', (event) => {
			if (typeof event.value?.postalCode === 'string') {
				this.enteredPostalCode = event.value.postalCode;
			}
			const displayError = document.getElementById('card-errors');
			if (!displayError) {
				return;
			}

			if (event.error) {
				displayError.textContent = event.error.message;
				return;
			}

			displayError.textContent = '';
		});
	},

	destroy() {
		if (this.cardElement) {
			try {
				this.cardElement.destroy();
			} catch (e) {
				// ignore
			}
			this.cardElement = null;
		}
		this.elements = null;
		const cardContainer = document.getElementById('card-element');
		if (cardContainer) {
			cardContainer.innerHTML = '';
		}
	},

	async submitCard() {
		if (this.isProcessing || !this.stripe || !this.cardElement) {
			return;
		}

		let details;
		try {
			details = payerBillingDetails(this.billingDetails, this.payerName, this.enteredPostalCode);
		} catch (error) {
			const displayError = document.getElementById('card-errors');
			if (displayError) {
				displayError.textContent = error.message;
			}
			return;
		}

		this.isProcessing = true;

		try {
			const { setupIntent, error } = await this.stripe.confirmCardSetup(this.clientSecret, {
				payment_method: {
					card: this.cardElement,
					billing_details: details,
				},
			});

			if (error) {
				const displayError = document.getElementById('card-errors');
				if (displayError) {
					displayError.textContent = error.message;
				}
			} else if (setupIntent) {
				this.$wire.cardAdded();
			}
		} catch (e) {
			const displayError = document.getElementById('card-errors');
			if (displayError) {
				displayError.textContent = 'An unexpected error occurred.';
			}
		}

		this.isProcessing = false;
	},
});

window.invoicePaymentManager = (...args) => {
	const {
		stripeClientSecret,
		customerSessionClientSecret = null,
		stripeKey,
		invoicesListUrl = '',
		billingDetails = {},
	} = paymentAlpineOptions(args, [
		'stripeClientSecret',
		'customerSessionClientSecret',
		'stripeKey',
		'invoicesListUrl',
	]);

	return {
				billingDetails,
				payerName: billingDetails.name ?? '',
				selectedSavedPaymentMethodId: null,
				selectedPaymentMethodType: null,
				stripe: null,
				elements: null,
				paymentElement: null,
				paymentElementReady: false,
				// Tracks whether Stripe's Payment Element fired its `loaderror`
				// event during bootstrap. Distinct from paymentElementReady so
				// submitStripePayment() can tell "still loading" apart from
				// "genuinely failed to load" (extension blocker, CSP, etc.) and
				// surface the right customer-facing message.
				paymentLoadError: false,
				paymentLoadErrorMessage: null,
				stripeInitialized: false,
				currentClientSecret: stripeClientSecret,
				currentCustomerSessionClientSecret: customerSessionClientSecret,
				lastMountedCustomerSessionSecret: null,
				stripeKey,
				invoicesListUrl,
				paypalButtonsInstance: null,
				paypalInitialized: false,
				paypalBlocked: false,
				stripeBlocked: false,
				stripeInitAttempts: 0,
				paypalInitAttempts: 0,
				maxInitAttempts: 30,

				shouldCollectPayment() {
					return !!this.currentClientSecret
						|| !!document.getElementById('paypal-invoice-button')
						|| !!document.getElementById('stripe-payment-element');
				},

				getActiveClientSecret() {
					return this.currentClientSecret;
				},

				mountPaymentSdks(options = {}) {
					return mountCheckoutPaymentSdks(this, options, {
						ensureSdks: ensureCheckoutPaymentSdks,
					});
				},

				destroyStripe() {
					if (this.paymentElement) {
						try {
							this.paymentElement.destroy();
						} catch (e) {
							// ignore destroy errors
						}
						this.paymentElement = null;
					}

					this.elements = null;
					this.stripeInitialized = false;
					this.paymentElementReady = false;
					this.paymentLoadError = false;
					this.paymentLoadErrorMessage = null;
					this.lastMountedCustomerSessionSecret = null;

					const stripeContainer = document.getElementById('stripe-payment-element');
					if (stripeContainer) {
						stripeContainer.innerHTML = '';
					}
				},

				init() {
					applyBillingDetailsFromDom(this);
					// Pay uses Blade @click="handleStripePayment" only — do not also
					// addEventListener here or one click fires handleStripePayment twice.

					this.$nextTick(() => {
						this.mountPaymentSdks();
					});

					this._onInvoiceLivewireNavigated = () => {
						this.$nextTick(() => {
							this.mountPaymentSdks({ forceRemount: true });
						});
					};
					document.addEventListener('livewire:navigated', this._onInvoiceLivewireNavigated);

					this.$wire.on('stripe-reinitialize', (event) => {
						if (!event?.clientSecret) {
							return;
						}

						this.stripeInitialized = false;
						this.currentClientSecret = event.clientSecret;
						this.currentCustomerSessionClientSecret =
							event.customerSessionClientSecret !== undefined
								? event.customerSessionClientSecret
								: this.$wire?.get('customerSessionClientSecret') ?? null;

						this.$nextTick(() => {
							this.mountPaymentSdks({ forceRemount: true });
						});
					});

					this.$wire.on('stripe-clear', () => {
						this.destroyStripe();
						this.currentClientSecret = null;
						this.currentCustomerSessionClientSecret = null;
					});

					this.$wire.on('payment-success', () => {
						this.setButtonState('success', 'Payment complete! Redirecting...');
					});

					this.$wire.on('payment-error', (event) => {
						this.setButtonState('error', livewireEventMessage(event));
						setTimeout(() => this.setButtonState('ready'), 3000);
					});
				},

				destroy() {
					if (this._onInvoiceLivewireNavigated) {
						document.removeEventListener('livewire:navigated', this._onInvoiceLivewireNavigated);
						this._onInvoiceLivewireNavigated = null;
					}
					teardownCheckoutPaymentWidgets(this);
				},

				initAllPaymentMethods() {
					return this.mountPaymentSdks();
				},

				initStripeWithRetry() {
					if (this.stripeInitialized) {
						return;
					}

					this.stripeInitAttempts = 0;
					this.tryInitStripe();
				},

				tryInitStripe() {
					const container = document.getElementById('stripe-payment-element');
					if (!container) {
						if (this.stripeInitAttempts < this.maxInitAttempts) {
							this.stripeInitAttempts++;
							setTimeout(() => this.tryInitStripe(), 100);
						}
						return;
					}

					if (typeof Stripe !== 'undefined' && this.currentClientSecret) {
						this.initStripe(this.currentClientSecret);
						return;
					}

					if (this.stripeInitAttempts < this.maxInitAttempts) {
						this.stripeInitAttempts++;
						ensureStripeSdk().then(() => this.tryInitStripe()).catch(() => {
							setTimeout(() => this.tryInitStripe(), 100);
						});
						return;
					}

					this.stripeBlocked = true;
					console.error('Stripe.js failed to load after maximum attempts');
				},

				initPayPalWithRetry() {
					if (this.paypalInitialized) {
						return;
					}

					this.paypalInitAttempts = 0;
					this.tryInitPayPal();
				},

				tryInitPayPal() {
					const container = document.getElementById('paypal-invoice-button');
					if (!container) {
						if (this.paypalInitAttempts < this.maxInitAttempts) {
							this.paypalInitAttempts++;
							setTimeout(() => this.tryInitPayPal(), 100);
						}
						return;
					}

					if (typeof paypal !== 'undefined') {
						this.initPayPal();
						return;
					}

					if (this.paypalInitAttempts < this.maxInitAttempts) {
						this.paypalInitAttempts++;
						const clientId = resolvePayPalClientId(this);
						if (!clientId) {
							setTimeout(() => this.tryInitPayPal(), 100);
							return;
						}

						ensurePayPalSdk({
							clientId,
							currency: container.getAttribute('data-paypal-currency') || 'USD',
						}).then(() => this.tryInitPayPal()).catch(() => {
							setTimeout(() => this.tryInitPayPal(), 100);
						});
						return;
					}

					this.paypalBlocked = true;
					console.error('PayPal SDK failed to load after maximum attempts');
				},

				/**
				 * Livewire redirect from wire calls is not always applied when using await;
				 * navigate if we are still on an invoice pay URL after success handling.
				 */
				scheduleInvoicesRedirectIfStillOnPayPage() {
					if (!this.invoicesListUrl) {
						return;
					}

					setTimeout(() => {
						if (/\/invoices\/[^/]+\/pay/.test(window.location.pathname)) {
							window.location.assign(this.invoicesListUrl);
						}
					}, 400);
				},

				initPayPal() {
					const container = document.getElementById('paypal-invoice-button');
					if (!container || typeof paypal === 'undefined') {
						return;
					}

					if (this.paypalInitialized && this.paypalButtonsInstance) {
						return;
					}

					container.innerHTML = '';

					this.paypalButtonsInstance = paypal.Buttons({
						style: {
							shape: 'rect',
							color: 'blue',
							layout: 'vertical',
							label: 'pay',
						},

						createOrder: async () => {
							try {
								return await this.$wire.createPayPalInvoiceOrder();
							} catch (error) {
								this.$wire.dispatch('paymentFailed', {
									message: error?.message || 'Failed to create PayPal order',
									gateway: 'paypal',
									code: 'create_order_failed',
									flow: 'invoice_paypal',
								});
								throw error;
							}
						},

						onApprove: async (data) => {
							try {
								await this.$wire.capturePayPalInvoiceOrder(data.orderID);
								this.scheduleInvoicesRedirectIfStillOnPayPage();
							} catch (error) {
								this.$wire.dispatch('paymentFailed', {
									message: error?.message || 'Failed to complete payment',
									gateway: 'paypal',
									paypalOrderId: data?.orderID ?? null,
									code: 'capture_failed',
									flow: 'invoice_paypal',
								});
							}
						},

						onCancel: () => {
							this.$wire.dispatch('banner-message', {
								style: 'warning',
								message: 'PayPal payment was cancelled.',
							});
						},

						onError: (err) => {
							this.$wire.dispatch('paymentFailed', livewirePayPalFailurePayload(err, 'invoice_paypal'));
						},
					});

					this.paypalButtonsInstance
						.render('#paypal-invoice-button')
						.then(() => {
							this.paypalInitialized = true;
						})
						.catch((err) => {
							console.error('PayPal render error:', err);
						});
				},

				initStripe(clientSecret = null) {
					const secret = clientSecret || this.currentClientSecret;
					const container = document.getElementById('stripe-payment-element');

					if (!secret || !container || typeof Stripe === 'undefined') {
						return;
					}

					const csSession = this.currentCustomerSessionClientSecret;
					if (
						this.stripeInitialized &&
						this.currentClientSecret === secret &&
						this.lastMountedCustomerSessionSecret === csSession &&
						this.paymentElement
					) {
						return;
					}

					container.innerHTML = '';

					if (this.paymentElement) {
						try {
							this.paymentElement.destroy();
						} catch (e) {
							// ignore
						}
						this.paymentElement = null;
					}
					this.paymentElementReady = false;
					this.paymentLoadError = false;
					this.paymentLoadErrorMessage = null;

					if (!this.stripe) {
						this.stripe = Stripe(this.stripeKey);
					}

					const options = {
						clientSecret: secret,
						appearance: {
							theme: document.documentElement.classList.contains('dark') ? 'night' : 'stripe',
							variables: {
								colorPrimary: '#6366f1',
							},
						},
					};

					if (csSession) {
						options.customerSessionClientSecret = csSession;
					}

					this.elements = this.stripe.elements(options);
					this.selectedSavedPaymentMethodId = null;
					this.selectedPaymentMethodType = null;
					this.paymentElement = this.elements.create('payment', { defaultValues: { billingDetails: this.billingDetails }, fields: { billingDetails: { name: 'never' } } });
					// Subscribe to `ready` / `loaderror` BEFORE mount() — Stripe
					// fires these asynchronously and does not replay them for late
					// subscribers, so listening post-mount can race and
					// permanently miss the event.
					this.paymentElement.on('change', (event) => {
						this.selectedSavedPaymentMethodId = event.value?.payment_method?.id ?? null;
						this.selectedPaymentMethodType = event.value?.type ?? null;
					});
					this.paymentElement.on('ready', () => {
						this.paymentElementReady = true;
					});
					this.paymentElement.on('loaderror', (event) => {
						// `loaderror` means Stripe's iframe couldn't bootstrap
						// (adblocker/privacy extension blocking Stripe's CDN,
						// CSP, offline, firewall, etc.). Record it so
						// submitStripePayment() can surface a blocker-specific
						// message rather than a generic "form is still loading".
						this.paymentLoadError = true;
						this.paymentLoadErrorMessage = event?.error?.message || 'Payment Element loaderror (no message)';
					});
					this.paymentElement.mount('#stripe-payment-element');
					this.currentClientSecret = secret;
					this.lastMountedCustomerSessionSecret = csSession;
					this.stripeInitialized = true;
				},

				// See checkoutPayment.waitForStripeElementReady — same rationale.
				// Only resolves true when Stripe emits the real `ready` event;
				// no DOM-based fallback — an iframe being present doesn't mean
				// Stripe has finished bootstrapping inside it, and silently
				// letting confirmPayment() run produces the exact "Element not
				// mounted / ready has not been emitted" error we are trying
				// to prevent. Short-circuits to false if Stripe's Payment
				// Element has already fired `loaderror`, so we surface a
				// blocker-specific error immediately instead of waiting 10s.
				waitForStripeElementReady(timeoutMs = 10000) {
					if (this.paymentElementReady) {
						return Promise.resolve(true);
					}
					if (this.paymentLoadError) {
						return Promise.resolve(false);
					}

					return new Promise((resolve) => {
						const start = Date.now();
						const tick = () => {
							if (this.paymentElementReady) {
								resolve(true);
								return;
							}
							if (this.paymentLoadError) {
								resolve(false);
								return;
							}
							if (Date.now() - start >= timeoutMs) {
								resolve(false);
								return;
							}
							setTimeout(tick, 50);
						};
						tick();
					});
				},

				// Snapshot of client-side payment-element state at the moment
				// we decide it's not ready. Forwarded to Sentry via the
				// paymentFailed dispatch so the next occurrence of
				// ATTORIC-NET-F2 tells us *why* ready didn't fire (SDK
				// blocked, iframe missing, load error string, etc.) instead
				// of just "it didn't".
				buildStripeReadinessDiagnostics() {
					const container = document.getElementById('stripe-payment-element');
					const iframe = container?.querySelector('iframe');
					return {
						sdkLoaded: typeof Stripe !== 'undefined',
						elementCreated: !!this.paymentElement,
						elementReady: !!this.paymentElementReady,
						loadErrorFired: !!this.paymentLoadError,
						loadErrorMessage: this.paymentLoadErrorMessage,
						iframePresent: !!iframe,
						iframeSrc: iframe?.getAttribute('src') || null,
						containerConnected: !!(container && container.isConnected),
						clientSecretPresent: !!this.currentClientSecret,
						customerSessionPresent: !!this.currentCustomerSessionClientSecret,
						viewportWidth: window.innerWidth,
						viewportHeight: window.innerHeight,
						userAgent: navigator.userAgent?.slice(0, 300) || null,
						cspEnforcedScriptViolationSeen: checkoutEnforcedScriptCspViolationSeen,
						cspPaymentViolations: paymentCspViolationSnapshot(),
					};
				},

				setButtonState(state, message = '') {
					const button = document.getElementById('stripe-pay-button');
					const statusMessage = document.getElementById('payment-status-message');

					if (!button) {
						return;
					}

					button.dataset.state = state;

					button.querySelectorAll('[data-show]').forEach((span) => {
						span.classList.add('hidden');
					});

					const activeSpan = button.querySelector(`[data-show="${state}"]`);
					if (activeSpan) {
						activeSpan.classList.remove('hidden');
					}

					button.disabled = ['processing', 'confirming', 'success'].includes(state);

					button.classList.remove('bg-indigo-600', 'hover:bg-indigo-700', 'bg-emerald-600', 'bg-red-600', 'hover:bg-red-700');

					switch (state) {
						case 'ready':
							button.classList.add('bg-indigo-600', 'hover:bg-indigo-700');
							break;
						case 'processing':
						case 'confirming':
							button.classList.add('bg-indigo-600');
							break;
						case 'success':
							button.classList.add('bg-emerald-600');
							break;
						case 'error':
							button.classList.add('bg-red-600', 'hover:bg-red-700');
							break;
					}

					if (!statusMessage) {
						return;
					}

					if (message) {
						statusMessage.textContent = message;
						statusMessage.classList.remove('hidden');
						statusMessage.className = `mt-3 text-center text-sm ${state === 'error' ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`;
						return;
					}

					statusMessage.classList.add('hidden');
				},

				async handleStripePayment() {
					// Sync double-submit guard. A second Pay click landing in the
					// window between the first click and setButtonState('processing')
					// would otherwise slip through every async check and call
					// confirmPayment() twice — the second call landing on a PI that
					// had already succeeded produced payment_intent_unexpected_state
					// errors ("A processing error occurred." / invalid_request_error)
					// while the success path continued in parallel. This is the
					// "button flashed succeeded → failed → redirect" bug.
					if (this._stripeSubmitInFlight) {
						return;
					}
					this._stripeSubmitInFlight = true;

					// Immediately disable the Pay button at the DOM level so the
					// user gets visual feedback on the first click AND the native
					// click handler stops firing for subsequent presses. This
					// closes the nervous-double-click window before the async
					// buttonState transitions catch up. The normal setButtonState
					// flow will keep it disabled through processing/confirming/success.
					const payButton = document.getElementById('stripe-pay-button');
					if (payButton) {
						payButton.disabled = true;
					}

					try {
						if (!this.stripe || !this.elements) {
							this.setButtonState('error');
							this.$wire.dispatch('banner-message', {
								style: 'danger',
								message: 'Payment system not ready. Please refresh the page.',
							});
							return;
						}

						// Wait for the Payment Element's `ready` event — confirmPayment()
						// throws "We could not retrieve data from the specified Element..."
						// if it runs before Elements has finished mounting (common when the
						// user clicks Pay right after the server remints the PI).
					const ready = await this.waitForStripeElementReady();
					if (!ready) {
						this.setButtonState('error');
						// Distinguish an explicit loaderror (Stripe told us the
						// iframe failed to bootstrap — blocker/network/CSP) from
						// a silent timeout so the server can pick the right
						// recovery strategy and message.
						const loadErrored = !!this.paymentLoadError;
						const diagnostics = this.buildStripeReadinessDiagnostics?.() ?? null;
						this.$wire.dispatch('paymentFailed', {
							message: loadErrored
								? ('Stripe Payment Element reported loaderror: '+(this.paymentLoadErrorMessage || 'unknown'))
								: 'Stripe Payment Element did not emit ready() within 10s.',
							gateway: 'stripe',
							code: loadErrored ? 'element_load_error' : 'element_not_ready',
							paymentIntentId: this.$wire?.get('stripePaymentIntentId') ?? null,
							flow: 'invoice_stripe_payment',
							diagnostics,
						});
						setTimeout(() => this.setButtonState('ready'), 3000);
						return;
					}

					this.setButtonState('processing', 'Securely processing your card...');

					const activeElements = this.elements;
					const activeSecret = this.currentClientSecret;

					try {
						const creditDecision = await abortStripeConfirmIfCreditApplyFailed(this, 'invoice_stripe_payment');
						if (creditDecision.abort) {
							return;
						}

						const paymentElementConnected = !!document.getElementById('stripe-payment-element')?.querySelector('iframe');

						if (
							stripeElementsSessionChanged({
								stripe: this.stripe,
								elements: this.elements,
								activeElements,
								activeSecret,
								stillActiveSecret: this.currentClientSecret,
								paymentElementConnected,
							})
						) {
							this.setButtonState('error');
							this.$wire.dispatch('paymentFailed', {
								message: 'Payment form refreshed. Please click Pay again.',
								gateway: 'stripe',
								code: 'elements_remounted',
								flow: 'invoice_stripe_payment',
								paymentIntentId: this.$wire?.get('stripePaymentIntentId') ?? null,
							});
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						const { error, paymentIntent } = await this.stripe.confirmPayment({
							elements: activeElements,
							confirmParams: {
								return_url: window.location.href,
								...payerConfirmParams(this.billingDetails, this.payerName, this.selectedSavedPaymentMethodId, this.selectedPaymentMethodType),
							},
							redirect: 'if_required',
						});

						if (error) {
							this.setButtonState('error');
							this.$wire.dispatch('paymentFailed', livewireStripeFailurePayload(error, 'invoice_stripe_payment'));
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						if (paymentIntent?.status === 'succeeded') {
							this.setButtonState('success', 'Payment complete! Redirecting you now...');
							await this.$wire.paymentSucceeded(paymentIntent.id);
							this.scheduleInvoicesRedirectIfStillOnPayPage();
							return;
						}

						if (paymentIntent?.status === 'processing') {
							this.setButtonState('confirming', 'Payment is being processed by your bank...');
							setTimeout(() => {
								this.setButtonState('confirming', 'Still waiting for bank confirmation. You can wait or check back later.');
							}, 5000);
							return;
						}

						if (paymentIntent?.status === 'requires_action') {
							this.setButtonState('confirming', 'Additional authentication required...');
						}
					} catch (e) {
						this.setButtonState('error');
						this.$wire.dispatch('paymentFailed', stripeConfirmExceptionPayload(e, 'invoice_stripe_payment', {
							paymentIntentId: e?.payment_intent?.id ?? this.$wire?.get('stripePaymentIntentId') ?? null,
							activeSecret,
							stillActiveSecret: this.currentClientSecret,
							elementsIdentityChanged: this.elements !== activeElements,
							paymentElementConnected: !!document.getElementById('stripe-payment-element')?.querySelector('iframe'),
							diagnostics: this.buildStripeReadinessDiagnostics?.() ?? null,
						}));
						setTimeout(() => this.setButtonState('ready'), 3000);
					}
					} finally {
						this._stripeSubmitInFlight = false;
					}
				},
			};
};

			window.checkoutPayment = (...args) => {
	const {
		stripeClientSecret = null,
		customerSessionClientSecret = null,
		stripeKey = null,
		initialPaymentMethod = null,
		stripeSetupClientSecret = null,
		isSetupMode = false,
		billingDetails = {},
	} = paymentAlpineOptions(args, [
		'stripeClientSecret',
		'customerSessionClientSecret',
		'stripeKey',
		'initialPaymentMethod',
		'stripeSetupClientSecret',
		'isSetupMode',
	]);

	return {
				billingDetails,
				payerName: billingDetails.name ?? '',
				selectedSavedPaymentMethodId: null,
				selectedPaymentMethodType: null,
				stripe: null,
				elements: null,
				cardElement: null,
				cardElementReady: false,
				isProcessing: false,
				clientSecret: stripeClientSecret,
				customerSessionClientSecret,
				setupClientSecret: stripeSetupClientSecret,
				isSetupMode: isSetupMode,
				stripeInitialized: false,
				stripeBlocked: false,
				stripeLoadError: false,
				stripeInitTimedOut: false,
				// Captured from Stripe Payment Element's `loaderror` event so
				// we can forward it to Sentry and pick a blocker-specific
				// customer-facing message instead of a generic timeout.
				stripeLoadErrorMessage: null,
				stripeLoadRetries: 0,
				checkoutInitStarted: false,
				readinessTimer: null,
				reportedInitStage: null,
				lastStripeInitFingerprint: null,
				paymentMethod: initialPaymentMethod,
				stripeKey,
				useCredits: false,
				creditsAppliedAmount: 0,
				remainingAfterCredits: 0,
				paypalButtonsInstance: null,
				paypalInitialized: false,
				paypalBlocked: false,
				paypalLoadRetries: 0,
				paypalRenderError: false,
				buttonState: 'ready',
				statusMessage: '',
				// Synchronous click-level guard used by handlePayment() AND bound
				// into the Pay button's x-bind:disabled. buttonState flips async
				// (after waitForStripeElementReady etc.) which leaves a ~100ms+
				// window where a second click can slip through; this flag
				// closes that window at the DOM layer and gives instant visual
				// feedback that the click was received.
				payClickInFlight: false,

				shouldCollectPayment() {
					if (this.isSetupMode) return true;
					return this.remainingAfterCredits > 0 || !this.useCredits;
				},

				getActiveClientSecret() {
					return this.isSetupMode ? this.setupClientSecret : this.clientSecret;
				},

				mountPaymentSdks(options = {}) {
					return mountCheckoutPaymentSdks(this, options, {
						ensureSdks: ensureCheckoutPaymentSdks,
					});
				},

				init() {
					applyBillingDetailsFromDom(this);
					if (!beginCheckoutInitialization(this)) return;
					this.syncFromWire();
					if (!this.paymentMethod) {
						const wirePaymentMethod = this.$wire?.get('paymentMethod');
						this.paymentMethod = wirePaymentMethod ?? 'stripe';
						if (wirePaymentMethod !== this.paymentMethod) {
							this.$wire?.set('paymentMethod', this.paymentMethod);
						}
					}

					// @push('scripts') only runs on full page load. Livewire updates /
					// wire:navigate can render step 4 without Stripe/PayPal globals —
					// load nonce'd SDKs on demand, then mount. Exclusive lock prevents
					// navigated remount from racing this first mount.
					this.$nextTick(() => {
						this.mountPaymentSdks();
					});

					this._onCheckoutLivewireNavigated = () => {
						this.$nextTick(() => {
							// Tear down via forceRemount (destroyStripe + PayPal close)
							// before remounting; serialized with init()'s mount.
							this.mountPaymentSdks({ forceRemount: true });
						});
					};
					document.addEventListener('livewire:navigated', this._onCheckoutLivewireNavigated);

					this.$watch('remainingAfterCredits', () => {
						this.$nextTick(() => {
							if (!this.shouldCollectPayment()) {
								this.stripeLoadError = false;
								this.stripeInitTimedOut = false;
								this.stripeBlocked = false;
								return;
							}
							this.mountPaymentSdks();
						});
					});

					this.$watch('useCredits', () => {
						this.$nextTick(() => {
							if (!this.shouldCollectPayment()) {
								this.stripeLoadError = false;
								this.stripeInitTimedOut = false;
								this.stripeBlocked = false;
								return;
							}
							this.mountPaymentSdks();
						});
					});

					this.$watch('paymentMethod', (value) => {
						this.$wire?.set('paymentMethod', value);
						if (!this.shouldCollectPayment()) {
							return;
						}

						if (value === 'stripe') {
							this.watchStripeReadiness();
							this.initStripe();
							return;
						}

						if (value === 'paypal') {
							this.initPayPal();
						}
					});

					if (typeof Livewire !== 'undefined' && Livewire?.hook) {
						Livewire.hook('commit', ({ succeed }) => {
							succeed(() => {
								this.$nextTick(() => {
									if (this._stripeSubmitInFlight || this.payClickInFlight) {
										return;
									}

									const prevActiveSecret = this.getActiveClientSecret();
									const prevCustomerSession = this.customerSessionClientSecret;
									this.syncFromWire();
									if (!this.shouldCollectPayment()) {
										return;
									}

									const secretChanged =
										prevActiveSecret !== this.getActiveClientSecret() ||
										prevCustomerSession !== this.customerSessionClientSecret;

									// Only remount when secrets actually change — not when
									// `stripeInitialized` is still false. On first paint,
									// `init()`'s $nextTick already calls initStripe(); this
									// hook's first commit often fires in the same frame while
									// bootstrap is mid-flight. The old condition
									// `!stripeInitialized || secretChanged` ran a second
									// initStripe() in parallel, destroyed Element A before it
									// emitted `ready`, and wedged the Payment Element
									// (ATT-59 / ATTORIC-NET-F2). Invoice pay avoids this
									// because it does not use this commit hook.
									if (this.getActiveClientSecret() && secretChanged) {
										this.initStripe();
									}

									if (!this.paypalInitialized) {
										this.initPayPal();
									}
								});
							});
						});
					}

					Livewire.on('stripeClientSecretUpdated', (data) => {
						const secret = Array.isArray(data) ? data[0] : data;
						this.clientSecret = secret;
						const wireCs = this.$wire?.get('customerSessionClientSecret');
						if (typeof wireCs !== 'undefined') {
							this.customerSessionClientSecret = wireCs;
						}
						if (!secret) {
							// Server cleared the client_secret (e.g. initializeStripe failed
							// after cancelling the previous PI). Tear down Elements so a
							// subsequent Pay click can't confirm the stale client_secret.
							this.destroyStripe();
							return;
						}
						// Never destroy+remount Elements while confirmPayment is in
						// flight — that is the checkout js_exception /
						// requires_payment_method race. submitStripePayment() will
						// see the secret change and ask the user to click Pay again.
						if (this._stripeSubmitInFlight) {
							return;
						}
						if (this.shouldCollectPayment()) {
							this.$nextTick(() => this.initStripe());
						}
					});

					Livewire.on('stripe-invalidate', () => {
						// Explicit teardown event — used when initializeStripe throws
						// after cancelling the previous PaymentIntent so the browser
						// stops holding a canceled client_secret in Stripe Elements.
						this.clientSecret = null;
						this.setupClientSecret = null;
						this.customerSessionClientSecret = null;
						this.destroyStripe();
						this.setButtonState('error');
						setTimeout(() => this.setButtonState('ready'), 3000);
					});

					Livewire.on('reinitialize-stripe', () => {
						// Defensive legacy handler. The server no longer
						// dispatches this event — `stripeClientSecretUpdated`
						// alone drives remounts via initStripe()'s
						// fingerprint guard. We kept the listener so a
						// cached/old Livewire payload can't throw, but
						// deliberately DROPPED the forced destroyStripe()
						// and 100ms setTimeout: those, combined with the
						// other initStripe() path firing from
						// `stripeClientSecretUpdated`, used to tear down
						// Stripe's Payment Element while it was still
						// bootstrapping and leave it permanently stuck
						// in "not ready" (ATTORIC-NET-F2 on the
						// discount-apply flow).
						this.$nextTick(() => {
							this.syncFromWire();
							if (this.getActiveClientSecret() && this.shouldCollectPayment()) {
								this.initStripe();
							}
						});
					});

					Livewire.on('payment-success', () => {
						this.setButtonState('success', 'Payment complete! Redirecting...');
					});

					Livewire.on('payment-error', (event) => {
						const message = livewireEventMessage(event);
						this.setButtonState('error', message);
						const cardErrors = document.getElementById('stripe-card-errors');
						if (cardErrors && message) {
							cardErrors.textContent = message;
						}
						setTimeout(() => this.setButtonState('ready'), 3000);
					});
				},

				destroy() {
					if (this._onCheckoutLivewireNavigated) {
						document.removeEventListener('livewire:navigated', this._onCheckoutLivewireNavigated);
						this._onCheckoutLivewireNavigated = null;
					}
					teardownCheckoutPaymentWidgets(this);
				},

				syncFromWire() {
					const wireUseCredits = this.$wire?.get('useCredits');
					if (typeof wireUseCredits !== 'undefined' && wireUseCredits !== this.useCredits) {
						this.useCredits = wireUseCredits;
					}

					const wireCreditsApplied = this.$wire?.get('creditsAppliedAmountValue');
					if (typeof wireCreditsApplied !== 'undefined' && wireCreditsApplied !== this.creditsAppliedAmount) {
						this.creditsAppliedAmount = wireCreditsApplied;
					}

					const wireRemaining = this.$wire?.get('remainingAfterCreditsValue');
					if (typeof wireRemaining !== 'undefined' && wireRemaining !== this.remainingAfterCredits) {
						this.remainingAfterCredits = wireRemaining;
					}

					const wireClientSecret = this.$wire?.get('stripeClientSecret');
					if (typeof wireClientSecret !== 'undefined' && wireClientSecret !== this.clientSecret) {
						this.clientSecret = wireClientSecret;
					}

					const wireSetupSecret = this.$wire?.get('stripeSetupClientSecret');
					if (typeof wireSetupSecret !== 'undefined' && wireSetupSecret !== this.setupClientSecret) {
						this.setupClientSecret = wireSetupSecret;
					}

					const wireSetupMode = this.$wire?.get('isSetupMode');
					if (typeof wireSetupMode !== 'undefined' && wireSetupMode !== this.isSetupMode) {
						this.isSetupMode = wireSetupMode;
					}

					const wireCustomerSession = this.$wire?.get('customerSessionClientSecret');
					if (typeof wireCustomerSession !== 'undefined' && wireCustomerSession !== this.customerSessionClientSecret) {
						this.customerSessionClientSecret = wireCustomerSession;
					}
				},

				setButtonState(state, message = '') {
					this.buttonState = state;
					this.statusMessage = message;
					this.isProcessing = ['processing', 'confirming', 'success'].includes(state);
				},

				destroyStripe() {
					if (this.cardElement) {
						try {
							this.cardElement.destroy();
						} catch (e) {
							// ignore
						}
						this.cardElement = null;
					}
					this.elements = null;
					this.stripeInitialized = false;
					this.lastStripeInitFingerprint = null;
					this.cardElementReady = false;
					// Clear any stale load-error state so a fresh initStripe()
					// attempt isn't immediately short-circuited by a prior
					// cycle's loaderror.
					this.stripeLoadError = false;
					this.stripeInitTimedOut = false;
					this.stripeLoadErrorMessage = null;
				},

				// Polls for the Stripe Payment Element `ready` event — confirmPayment()
				// throws "We could not retrieve data from the specified Element.
				// Please make sure the Element you are attempting to use is mounted
				// and the ready event has been emitted." if it runs before the
				// element has finished mounting. This most often hits users who
				// click Pay immediately after applying a discount (which remints
				// the PaymentIntent and remounts Elements).
				//
				// Returns true ONLY when Stripe emits the genuine `ready` event.
				// We deliberately do NOT fall back to "iframe is in the DOM" —
				// a mounted iframe can still be mid-bootstrap, and pretending
				// it's ready would reproduce the very error we're guarding against.
				// Short-circuits to false if Stripe's Element already fired
				// `loaderror` — at that point a 10s wait is just user-hostile
				// padding; we know the form failed to bootstrap.
				waitForStripeElementReady(timeoutMs = 10000) {
					if (this.cardElementReady) {
						return Promise.resolve(true);
					}
					if (this.stripeLoadError) {
						return Promise.resolve(false);
					}

					return new Promise((resolve) => {
						const start = Date.now();
						const tick = () => {
							if (this.cardElementReady) {
								resolve(true);
								return;
							}
							if (this.stripeLoadError) {
								resolve(false);
								return;
							}
							if (Date.now() - start >= timeoutMs) {
								resolve(false);
								return;
							}
							setTimeout(tick, 50);
						};
						tick();
					});
				},

				// Snapshot of client-side Stripe Element state captured when
				// we dispatch an element_not_ready / element_load_error event
				// to the server. Logged verbatim into Sentry so we can tell
				// *why* `ready` didn't fire on this occurrence (SDK not
				// loaded, iframe missing, loaderror with message, etc.).
				buildStripeReadinessDiagnostics() {
					const container = document.getElementById('stripe-card-element');
					const iframe = container?.querySelector('iframe');
					return {
						sdkLoaded: typeof Stripe !== 'undefined',
						elementCreated: !!this.cardElement,
						elementReady: !!this.cardElementReady,
						loadErrorFired: !!this.stripeLoadError,
						iframePresent: !!iframe,
						containerConnected: !!(container && container.isConnected),
						clientSecretPresent: !!this.getActiveClientSecret(),
						customerSessionPresent: !!this.customerSessionClientSecret,
						stripeInitialized: !!this.stripeInitialized,
						stripeBlocked: !!this.stripeBlocked,
						stripeLoadRetries: this.stripeLoadRetries,
						viewportWidth: window.innerWidth,
						viewportHeight: window.innerHeight,
					};
				},

				retryStripe() {
					this.stripeBlocked = false;
					this.stripeLoadRetries = 0;
					this.initStripe();
				},

				retryPayPal() {
					this.paypalBlocked = false;
					this.paypalLoadRetries = 0;
					this.initPayPal();
				},

				retryPayPalRender() {
					this.paypalRenderError = false;
					this.initPayPal();
				},

				reloadPage() {
					window.location.reload();
				},

				watchStripeReadiness() {
					if (this.readinessTimer) clearTimeout(this.readinessTimer);
					if (this.paymentMethod !== 'stripe') return;
					this.readinessTimer = setTimeout(() => {
						this.readinessTimer = null;
						if (this.paymentMethod !== 'stripe' || !this.shouldCollectPayment() || this.cardElementReady) return;
						this.stripeInitTimedOut = true;
						this.reportStripeInitializationFailure();
					}, 5000);
				},

				reportStripeInitializationFailure() {
					const state = {
						keyPresent: !!this.stripeKey,
						clientSecretPresent: !!this.getActiveClientSecret(),
						sdkLoaded: typeof Stripe !== 'undefined',
						elementCreated: !!this.cardElement,
						elementReady: !!this.cardElementReady,
						loadErrorFired: stripeLoadErrorFired(this),
						iframePresent: !!document.getElementById('stripe-card-element')?.querySelector('iframe'),
						cspEnforcedScriptViolationSeen: checkoutEnforcedScriptCspViolationSeen,
						cspPaymentViolations: paymentCspViolationSnapshot(),
					};
					const stage = stripeInitializationStage(state);
					if (!stage || !markStripeInitializationFailure(this, stage)) return;
					this.$wire?.reportStripeInitializationFailure?.(stage, stripeInitializationDiagnostics(state));
				},

				initStripe() {
					if (!this.shouldCollectPayment()) {
						return Promise.resolve();
					}

					const secret = this.getActiveClientSecret();
					if (!this.stripeKey || !secret) {
						return Promise.resolve();
					}

					if (typeof Stripe === 'undefined') {
						this.stripeLoadRetries++;
						if (this.stripeLoadRetries >= 30) {
							this.stripeBlocked = true;
							return Promise.resolve();
						}
						// Kick an idempotent on-demand load (CSP nonce'd) then retry —
						// keeps the existing 30×100ms retry window for slow networks.
						// Return the Promise so mountCheckoutPaymentSdks can await the
						// iframe mount instead of resolving with an empty card host.
						if (!this._stripeSdkEnsureStarted) {
							this._stripeSdkEnsureStarted = true;
							return ensureStripeSdk()
								.then(() => {
									this.stripeLoadRetries = 0;
									this._stripeSdkEnsureStarted = false;
									return this.initStripe();
								})
								.catch(() => {
									this._stripeSdkEnsureStarted = false;
									return new Promise((resolve) => {
										setTimeout(() => resolve(this.initStripe()), 100);
									});
								});
						}
						return new Promise((resolve) => {
							setTimeout(() => resolve(this.initStripe()), 100);
						});
					}
					this.stripeBlocked = false;
					this._stripeSdkEnsureStarted = false;

					const cardContainer = document.getElementById('stripe-card-element');
					if (!cardContainer) {
						return new Promise((resolve) => {
							setTimeout(() => resolve(this.initStripe()), 50);
						});
					}

					const fingerprint = `${secret}::${this.customerSessionClientSecret || ''}`;
					const canReuseExisting =
						this.stripeInitialized &&
						this.lastStripeInitFingerprint === fingerprint &&
						this.stripe &&
						this.elements &&
						this.cardElement &&
						cardContainer.isConnected;

					if (canReuseExisting) {
						if (this.stripeInitTimedOut) {
							this.stripeInitTimedOut = false;
							this.stripeLoadError = false;
							this.watchStripeReadiness();
						}
						return Promise.resolve();
					}

					this.stripeLoadError = false;
					this.stripeInitTimedOut = false;
					const displayErrorEl = document.getElementById('stripe-card-errors');
					if (displayErrorEl) {
						displayErrorEl.textContent = '';
					}

					this.destroyStripe();
					this.watchStripeReadiness();

					this.stripe = Stripe(this.stripeKey);
					const elementsOptions = {
						clientSecret: secret,
						appearance: {
							theme: document.documentElement.classList.contains('dark') ? 'night' : 'stripe',
							variables: {
								colorPrimary: '#6366f1',
								borderRadius: '8px',
							},
						},
					};
					if (this.customerSessionClientSecret) {
						elementsOptions.customerSessionClientSecret = this.customerSessionClientSecret;
					}
					this.elements = this.stripe.elements(elementsOptions);

					cardContainer.innerHTML = '';
					this.cardElementReady = false;
					this.selectedSavedPaymentMethodId = null;
					this.selectedPaymentMethodType = null;
					this.cardElement = this.elements.create('payment', { defaultValues: { billingDetails: this.billingDetails }, fields: { billingDetails: { name: 'never' } } });

					// IMPORTANT: attach event listeners BEFORE calling mount(). Stripe
					// fires `ready` asynchronously right after mount finishes, and
					// Element.on() does NOT replay events — if we subscribe after
					// the event has already fired we silently miss it forever and
					// end up timing out inside waitForStripeElementReady().
					this.cardElement.on('ready', () => {
						this.cardElementReady = true;
						this.stripeLoadError = false;
						this.stripeInitTimedOut = false;
						this.reportedInitStage = null;
						if (this.readinessTimer) clearTimeout(this.readinessTimer);
						this.readinessTimer = null;
					});

					this.cardElement.on('change', (event) => {
						this.selectedSavedPaymentMethodId = event.value?.payment_method?.id ?? null;
						this.selectedPaymentMethodType = event.value?.type ?? null;
						const displayError = document.getElementById('stripe-card-errors');
						if (displayError) {
							displayError.textContent = event.error ? event.error.message : '';
						}
					});

					this.cardElement.on('loaderror', (event) => {
						this.stripeLoadError = true;
						this.stripeInitTimedOut = false;
						this.stripeLoadErrorMessage = event?.error?.message || 'Payment Element loaderror (no message)';
						this.reportStripeInitializationFailure();
						const displayError = document.getElementById('stripe-card-errors');
						if (displayError) {
							displayError.textContent =
								event.error?.message || 'Payment form failed to load. Please refresh the page.';
						}
					});

					this.cardElement.mount('#stripe-card-element');
					this.stripeInitialized = true;
					this.lastStripeInitFingerprint = fingerprint;
					return Promise.resolve();
				},

				initPayPal() {
					if (!this.shouldCollectPayment() && !this.isSetupMode) {
						return Promise.resolve();
					}

					const container = document.getElementById('paypal-button-container');
					if (!container) {
						return Promise.resolve();
					}

					if (!container.isConnected) {
						return Promise.resolve();
					}

					if (typeof paypal === 'undefined') {
						this.paypalLoadRetries++;
						if (this.paypalLoadRetries >= 30) {
							this.paypalBlocked = true;
							container.innerHTML = '';
							return Promise.resolve();
						}
						if (!this._paypalSdkEnsureStarted) {
							const clientId = resolvePayPalClientId(this);
							if (!clientId) {
								return new Promise((resolve) => {
									setTimeout(() => resolve(this.initPayPal()), 100);
								});
							}
							this._paypalSdkEnsureStarted = true;
							return ensurePayPalSdk({
								clientId,
								vault: resolvePayPalVault(),
							})
								.then(() => {
									this.paypalLoadRetries = 0;
									this._paypalSdkEnsureStarted = false;
									return this.initPayPal();
								})
								.catch(() => {
									this._paypalSdkEnsureStarted = false;
									return new Promise((resolve) => {
										setTimeout(() => resolve(this.initPayPal()), 100);
									});
								});
						}
						return new Promise((resolve) => {
							setTimeout(() => resolve(this.initPayPal()), 100);
						});
					}
					this.paypalBlocked = false;
					this._paypalSdkEnsureStarted = false;

					if (this.paypalButtonsInstance && !this.paypalInitialized) {
						return Promise.resolve();
					}

					const paypalFramePresent = !!container.querySelector('iframe');

					if (this.paypalInitialized && this.paypalButtonsInstance && paypalFramePresent) {
						return Promise.resolve();
					}

					this.paypalRenderError = false;
					this.paypalInitialized = false;
					this.paypalButtonsInstance = null;

					container.innerHTML = '';

					this.paypalButtonsInstance = paypal.Buttons({
						style: {
							shape: 'rect',
							color: 'blue',
							layout: 'vertical',
							label: 'paypal',
						},

						createVaultSetupToken: async () => {
							try {
								return await this.$wire.createPayPalVaultSetup();
							} catch (error) {
								this.$wire.dispatch('paymentFailed', {
									message: error?.message || 'Failed to start PayPal setup',
									gateway: 'paypal',
									code: 'vault_setup_failed',
									flow: 'checkout_paypal_vault',
								});
								throw error;
							}
						},

						onApprove: async (data) => {
							try {
								this.setButtonState('processing', 'Completing PayPal setup...');
								const setupTokenId = data.vaultSetupToken || data.orderID;
								await this.$wire.paypalVaultApproved(setupTokenId);
								this.setButtonState('success', 'PayPal payment complete! Preparing your order...');
							} catch (error) {
								this.setButtonState('error');
								this.$wire.dispatch('paymentFailed', {
									message: error?.message || 'Failed to complete PayPal setup',
									gateway: 'paypal',
									paypalOrderId: data?.orderID ?? null,
									code: 'vault_approve_failed',
									flow: 'checkout_paypal_vault',
								});
								setTimeout(() => this.setButtonState('ready'), 3000);
							}
						},

						onCancel: () => {
							this.$wire.dispatch('banner-message', {
								style: 'warning',
								message: 'PayPal payment was cancelled.',
							});
						},

						onError: (err) => {
							console.error('PayPal error:', err);
							this.setButtonState('error');
							this.$wire.dispatch('paymentFailed', livewirePayPalFailurePayload(err, 'checkout_paypal_vault'));
							setTimeout(() => this.setButtonState('ready'), 3000);
						},
					});

					return this.paypalButtonsInstance
						.render('#paypal-button-container')
						.then(() => {
							this.paypalInitialized = true;
							this.paypalRenderError = false;
						})
						.catch((err) => {
							console.error('PayPal render error:', err);
							const msg = err?.message || String(err);
							const domRemoved =
								typeof msg === 'string' &&
								msg.toLowerCase().includes('container element removed from dom');

							this.paypalButtonsInstance = null;
							this.paypalInitialized = false;

							if (domRemoved) {
								return;
							}

							this.paypalRenderError = true;
							this.$wire?.dispatch('banner-message', {
								style: 'danger',
								message: 'PayPal could not display the payment button. Please refresh the page or try Stripe.',
							});
						});
				},

				async handlePayment() {
					// Upstream re-entrancy guard. The Pay button's x-bind:disabled
					// only reacts to buttonState, and buttonState doesn't flip to
					// 'processing' until AFTER an async wait (waitForStripeElementReady,
					// applyCreditsAndPay, etc.). A second click — from a nervous
					// double-tap, a mobile touchend+click, or an Enter-while-focused
					// — lands in that window and starts handlePayment() again, which
					// runs confirmPayment() twice. The second confirmation hits an
					// already-succeeded PI and Stripe returns
					// invalid_request_error / payment_intent_unexpected_state while
					// the first call's paymentSucceeded is still in flight, producing
					// the "button flashed succeeded → failed → eventually-redirect"
					// symptom. payClickInFlight is also bound into the button's
					// disabled attribute so the button visibly locks on first click
					// (see checkout.blade.php).
					if (this.payClickInFlight) {
						return;
					}
					this.payClickInFlight = true;

					try {
						this.syncFromWire();
						if (this.isSetupMode && this.paymentMethod === 'stripe') {
							await this.submitStripeSetup();
							return;
						}

						if (this.remainingAfterCredits <= 0 && this.useCredits) {
							this.setButtonState('processing', 'Applying credits...');
							let result = null;
							try {
								result = await this.$wire.applyCreditsAndPay();
							} catch (error) {
								result = { ok: false, error: error?.message ?? null };
							}
							if (result?.ok === false || result?.fully_paid !== true) {
								this.setButtonState('error');
								this.$wire.dispatch('paymentFailed', {
									message: result?.error || "We couldn't apply your account credits. Please try again.",
									gateway: 'stripe',
									code: 'credits_apply_failed',
									flow: 'checkout_credits_only',
								});
								setTimeout(() => this.setButtonState('ready'), 3000);
							}
							return;
						}

						if (this.paymentMethod === 'stripe' && this.remainingAfterCredits > 0) {
							await this.submitStripePayment();
							return;
						}

						if (this.paymentMethod === 'paypal' && this.remainingAfterCredits > 0) {
							alert('Please use the PayPal button to complete payment.');
						}
					} finally {
						this.payClickInFlight = false;
					}
				},

				async submitStripePayment() {
					// Synchronous double-submit guard. buttonState transitions are
					// async — a second Pay click landing in the ~100ms window
					// between the first click and setButtonState('processing')
					// passed the old `buttonState !== 'ready'` check and ran
					// confirmPayment() twice. The second call landed on a PI
					// that had already succeeded, which Stripe rejected with
					// `invalid_request_error` / payment_intent_unexpected_state,
					// so the UI flashed success → failed → eventually-redirect.
					// (ATTORIC-NET-F2's latest tail.)
					if (this._stripeSubmitInFlight) {
						return;
					}
					this._stripeSubmitInFlight = true;

					try {
						if (!this.stripe || !this.elements) {
							this.setButtonState('error');
							this.$wire.dispatch('banner-message', {
								style: 'danger',
								message: 'Payment system not ready. Please refresh the page.',
							});
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						if (this.buttonState !== 'ready' && this.buttonState !== 'error') {
							return;
						}

					// Wait for the Payment Element to emit its `ready` event before we
					// touch it — otherwise Stripe throws "We could not retrieve data
					// from the specified Element..." from confirmPayment(). This most
					// often hits users who click Pay immediately after applying a
					// discount (which remints the PaymentIntent and remounts Elements).
					const ready = await this.waitForStripeElementReady();
					if (!ready) {
						this.setButtonState('error');
						// Distinguish a genuine loaderror (extension blocker /
						// CSP / offline — Stripe TOLD us the iframe failed) from
						// a silent 10s timeout (could be slow network, could be
						// a deeper bug). Different codes drive different
						// server-side recovery strategies and different
						// customer-facing copy.
						const loadErrored = !!this.stripeLoadError;
						const diagnostics = this.buildStripeReadinessDiagnostics?.() ?? null;
						this.$wire.dispatch('paymentFailed', {
							message: loadErrored
								? ('Stripe Payment Element reported loaderror: '+(this.stripeLoadErrorMessage || 'unknown'))
								: 'Stripe Payment Element did not emit ready() within 10s.',
							gateway: 'stripe',
							code: loadErrored ? 'element_load_error' : 'element_not_ready',
							paymentIntentId: this.$wire?.get('stripePaymentIntentId') ?? null,
							flow: 'checkout_stripe_payment',
							diagnostics,
						});
						setTimeout(() => this.setButtonState('ready'), 3000);
						return;
					}

					this.setButtonState('processing', 'Securely processing your card...');

					const activeElements = this.elements;
					const activeSecret = this.getActiveClientSecret();
					const secretFingerprintBefore = stripeSecretFingerprint(activeSecret);

					try {
						// Always call applyCreditsAndPay when useCredits is on and a
						// card remainder remains. PHP no-ops when credits to apply
						// are $0. Abort confirmPayment if credits did not actually apply.
						this.syncFromWire();
						const creditDecision = await abortStripeConfirmIfCreditApplyFailed(this, 'checkout_stripe_payment');
						if (creditDecision.abort) {
							return;
						}

						const stillActiveSecret = this.getActiveClientSecret();
						const paymentElementConnected = !!document.getElementById('stripe-card-element')?.querySelector('iframe');

						if (
							stripeElementsSessionChanged({
								stripe: this.stripe,
								elements: this.elements,
								activeElements,
								activeSecret,
								stillActiveSecret,
								paymentElementConnected,
							})
						) {
							this.setButtonState('error');
							this.$wire.dispatch('paymentFailed', {
								message: 'Payment form refreshed. Please click Pay again.',
								gateway: 'stripe',
								code: 'elements_remounted',
								flow: 'checkout_stripe_payment',
								paymentIntentId: this.$wire?.get('stripePaymentIntentId') ?? null,
								diagnostics: {
									secretFingerprintBefore,
									secretFingerprintAfter: stripeSecretFingerprint(stillActiveSecret),
									elementsIdentityChanged: this.elements !== activeElements,
									paymentElementConnected,
								},
							});
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						const { error, paymentIntent } = await this.stripe.confirmPayment({
							elements: activeElements,
							confirmParams: {
								return_url: window.location.href,
								...payerConfirmParams(this.billingDetails, this.payerName, this.selectedSavedPaymentMethodId, this.selectedPaymentMethodType),
							},
							redirect: 'if_required',
						});

						if (error) {
							this.setButtonState('error');
							this.$wire.dispatch('paymentFailed', livewireStripeFailurePayload(error, 'checkout_stripe_payment'));
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						if (paymentIntent?.status === 'succeeded') {
							this.setButtonState('success', 'Payment complete! Preparing your order...');
							this.$wire.dispatch('paymentSucceeded', { paymentIntentId: paymentIntent.id });
							return;
						}

						if (paymentIntent?.status === 'processing') {
							this.setButtonState('confirming', 'Payment is being processed by your bank...');
							setTimeout(() => {
								if (this.buttonState === 'confirming') {
									this.statusMessage = 'Still waiting for bank confirmation. You can wait or check back later.';
								}
							}, 5000);
							return;
						}

						if (paymentIntent?.status === 'requires_action') {
							this.setButtonState('confirming', 'Additional authentication required...');
						}
					} catch (e) {
						this.setButtonState('error');
						const stillActiveSecret = this.getActiveClientSecret();
						this.$wire.dispatch('paymentFailed', stripeConfirmExceptionPayload(e, 'checkout_stripe_payment', {
							paymentIntentId: e?.payment_intent?.id ?? this.$wire?.get('stripePaymentIntentId') ?? null,
							activeSecret,
							stillActiveSecret,
							elementsIdentityChanged: this.elements !== activeElements,
							paymentElementConnected: !!document.getElementById('stripe-card-element')?.querySelector('iframe'),
							diagnostics: this.buildStripeReadinessDiagnostics?.() ?? null,
						}));
						setTimeout(() => this.setButtonState('ready'), 3000);
					}
					} finally {
						// Release the synchronous double-submit guard so a genuine
						// retry (after a decline, after a refresh banner, etc.)
						// isn't permanently locked out. The buttonState gate still
						// prevents re-entry while confirmPayment is mid-flight
						// via the success/processing/confirming states.
						this._stripeSubmitInFlight = false;
					}
				},

				async submitStripeSetup() {
					// Sync double-submit guard — same rationale as submitStripePayment.
					// A second click landing before setButtonState('processing')
					// otherwise slips past the buttonState gate and calls
					// confirmSetup() twice.
					if (this._stripeSubmitInFlight) {
						return;
					}
					this._stripeSubmitInFlight = true;

					try {
						if (!this.stripe || !this.elements) {
							this.setButtonState('error');
							this.$wire.dispatch('banner-message', {
								style: 'danger',
								message: 'Payment system not ready. Please refresh the page.',
							});
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						if (this.buttonState !== 'ready' && this.buttonState !== 'error') {
							return;
						}

					// Same `ready` gate as submitStripePayment — confirmSetup() throws the
					// same "Element not mounted / ready" error if called before the
					// Payment Element has emitted its ready event.
					const ready = await this.waitForStripeElementReady();
					if (!ready) {
						this.setButtonState('error');
						const loadErrored = !!this.stripeLoadError;
						const diagnostics = this.buildStripeReadinessDiagnostics?.() ?? null;
						this.$wire.dispatch('paymentFailed', {
							message: loadErrored
								? ('Stripe Payment Element reported loaderror: '+(this.stripeLoadErrorMessage || 'unknown'))
								: 'Stripe Payment Element did not emit ready() within 10s.',
							gateway: 'stripe',
							code: loadErrored ? 'element_load_error' : 'element_not_ready',
							flow: 'checkout_stripe_setup',
							diagnostics,
						});
						setTimeout(() => this.setButtonState('ready'), 3000);
						return;
					}

					this.setButtonState('processing', 'Saving your payment method...');

					try {
						const { error, setupIntent } = await this.stripe.confirmSetup({
							elements: this.elements,
							confirmParams: {
								return_url: window.location.href,
								...payerConfirmParams(this.billingDetails, this.payerName, this.selectedSavedPaymentMethodId, this.selectedPaymentMethodType),
							},
							redirect: 'if_required',
						});

						if (error) {
							this.setButtonState('error');
							this.$wire.dispatch('paymentFailed', livewireStripeFailurePayload(error, 'checkout_stripe_setup'));
							setTimeout(() => this.setButtonState('ready'), 3000);
							return;
						}

						if (setupIntent?.status === 'succeeded') {
							this.setButtonState('success', 'Payment method saved! Completing your order...');
							this.$wire.dispatch('setupIntentSucceeded', { setupIntentId: setupIntent.id });
							return;
						}

						if (setupIntent?.status === 'processing') {
							this.setButtonState('confirming', 'Verifying your payment method...');
						}

						if (setupIntent?.status === 'requires_action') {
							this.setButtonState('confirming', 'Additional authentication required...');
						}
					} catch (e) {
						this.setButtonState('error');
						this.$wire.dispatch('paymentFailed', {
							message: e?.message || 'Failed to save payment method. Please try again.',
							gateway: 'stripe',
							code: 'js_exception',
							setupIntentId: e?.setup_intent?.id ?? null,
							flow: 'checkout_stripe_setup',
						});
						setTimeout(() => this.setButtonState('ready'), 3000);
					}
					} finally {
						this._stripeSubmitInFlight = false;
					}
				},
			};
};

window.discordLinkPrompt = () => ({
	showBanner: false,

	init() {
		if (this.getCookie('attoric_discord_prompt_dismissed')) {
			return;
		}

		const reveal = () => {
			if (this.getCookie('attoric_discord_prompt_dismissed')) {
				return;
			}

			this.$nextTick(() => {
				setTimeout(() => {
					if (!this.getCookie('attoric_discord_prompt_dismissed')) {
						this.showBanner = true;
					}
				}, 300);
			});
		};

		if (this.getCookie('attoric_consent')) {
			reveal();
			return;
		}

		window.addEventListener(
			'cookie-consent-resolved',
			() => {
				reveal();
			},
			{ once: true },
		);
	},

	dismiss() {
		const expires = new Date();
		expires.setDate(expires.getDate() + 30);
		document.cookie = `attoric_discord_prompt_dismissed=1; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
		this.showBanner = false;
	},

	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop()?.split(';')?.shift() ?? null;
		}
		return null;
	},
});

window.cookieConsent = (gaId, gaDebug) => ({
	showBanner: false,
	gaId,
	gaDebug,

	init() {
		if (navigator.globalPrivacyControl === true) {
			this.setConsent('rejected');
			window.dispatchEvent(new CustomEvent('cookie-consent-resolved'));
			return;
		}

		const consent = this.getCookie('attoric_consent');

		if (!consent) {
			this.showBanner = true;
			return;
		}

		if (consent === 'accepted') {
			this.loadAnalytics();
		}
	},

	accept() {
		this.setConsent('accepted');
		this.showBanner = false;
		this.loadAnalytics();
		window.dispatchEvent(new CustomEvent('cookie-consent-resolved'));
	},

	reject() {
		this.setConsent('rejected');
		this.showBanner = false;
		window.dispatchEvent(new CustomEvent('cookie-consent-resolved'));
	},

	setConsent(value) {
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		document.cookie = `attoric_consent=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
	},

	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop()?.split(';')?.shift() ?? null;
		}
		return null;
	},

	loadAnalytics() {
		if (!this.gaId) {
			return;
		}

		if (window.gaLoaded) {
			return;
		}

		window.gaLoaded = true;

		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
		document.head.appendChild(script);

		window.dataLayer = window.dataLayer || [];
		window.gtag = function () {
			window.dataLayer.push(arguments);
		};

		window.gtag('js', new Date());

		const config = {
			anonymize_ip: true,
			allow_google_signals: false,
			allow_ad_personalization_signals: false,
		};

		if (this.gaDebug) {
			config.debug_mode = true;
		}

		window.gtag('config', this.gaId, config);
		window.dispatchEvent(new CustomEvent('analytics-loaded'));
	},
});

document.addEventListener('livewire:init', () => {
	registerLivewireBillingHydrate();

	const safeGtag = (event, data) => {
		if (typeof window.gtag === 'function') {
			window.gtag('event', event, data);
		}
	};

	Livewire.on('ga4-add-to-cart', (data) => {
		safeGtag('add_to_cart', {
			currency: data[0].currency,
			value: data[0].price,
			items: [
				{
					item_id: data[0].item_id,
					item_name: data[0].item_name,
					price: data[0].price,
					quantity: 1,
				},
			],
		});
	});

	Livewire.on('ga4-begin-checkout', (data) => {
		safeGtag('begin_checkout', data[0]);
	});

	Livewire.on('ga4-add-payment-info', (data) => {
		safeGtag('add_payment_info', data[0]);
	});

	Livewire.on('ga4-purchase', (data) => {
		safeGtag('purchase', data[0]);
	});
});

			window.ampWebSocketManager = (webSocketEndpoint) => ({
				ampWebSocket: null,
				endpoint: webSocketEndpoint,

				init() {
					if (!this.endpoint || typeof AmpWebSocket === 'undefined') {
						return;
					}

					this.ampWebSocket = new AmpWebSocket(this.endpoint);

					this.ampWebSocket.on('connectionStatus', (status) => {
						this.$wire.setConnectionStatus(status);
					});

					this.ampWebSocket.on('statusUpdate', (data) => {
						let totalInstances = 0;
						let runningInstances = 0;

						if (data?.targets) {
							data.targets.forEach((target) => {
								if (target.instances) {
									totalInstances += target.instances.length;
									target.instances.forEach((instance) => {
										if (instance.status?.running) {
											runningInstances++;
										}
									});
								}
							});
						}

						this.$wire.updateStats({
							totalInstances,
							runningInstances,
						});
					});

					const connectButton = document.getElementById('connect-websocket');
					if (connectButton) {
						connectButton.addEventListener('click', () => this.ampWebSocket.connect());
					}

					const disconnectButton = document.getElementById('disconnect-websocket');
					if (disconnectButton) {
						disconnectButton.addEventListener('click', () => this.ampWebSocket.disconnect());
					}

					this.ampWebSocket.connect();
				},
			});

			document.addEventListener('EchoReady', () => {
				if (typeof Livewire === 'undefined') {
					return;
				}

				document.querySelectorAll('[wire\\:id]').forEach((el) => {
					const component = Livewire.find(el.getAttribute('wire:id'));
					if (component) {
						component.$refresh();
					}
				});
			});

window.gameGallery = (totalGames, maxIndex) => ({
	currentIndex: 0,
	totalGames,
	cardWidth: 25,
	visibleCards: 4,
	maxIndex,
	resizeHandler: null,
	init() {
		this.updateResponsiveValues();
		this.$watch('currentIndex', () => this.updateTransform());
		this.resizeHandler = () => this.updateResponsiveValues();
		window.addEventListener('resize', this.resizeHandler);
	},
	destroy() {
		window.removeEventListener('resize', this.resizeHandler);
	},
	updateTransform() {
		const track = this.$el.querySelector('.carousel-track');
		if (track) {
			track.style.transform = 'translateX(-' + (this.currentIndex * this.cardWidth) + '%)';
			Array.from(track.children).forEach((card, index) => {
				card.inert = index < this.currentIndex || index >= this.currentIndex + this.visibleCards;
			});
		}
	},
	updateResponsiveValues() {
		const width = window.innerWidth;
		this.visibleCards = width >= 1024 ? 4 : width >= 768 ? 3 : width >= 640 ? 2 : 1;
		this.cardWidth = 100 / this.visibleCards;
		this.maxIndex = Math.max(0, this.totalGames - this.visibleCards);
		this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
		this.updateTransform();
	},
	next() {
		this.currentIndex = Math.min(this.currentIndex + 1, this.maxIndex);
		this.updateTransform();
	},
	prev() {
		this.currentIndex = Math.max(0, this.currentIndex - 1);
		this.updateTransform();
	},
	get rangeLabel() {
		if (! this.totalGames) {
			return 'No hosting services available';
		}

		const lastVisible = Math.min(this.totalGames, this.currentIndex + this.visibleCards);

		return 'Showing ' + (this.currentIndex + 1) + '–' + lastVisible + ' of ' + this.totalGames + ' hosting services';
	},
});

const setupNavigateProgress = () => {
	if (window.__attoricNavigateProgressInitialized) {
		return;
	}

	window.__attoricNavigateProgressInitialized = true;

	let hideTimeout = null;
	let removeTimeout = null;

	const getProgressBar = () => {
		let bar = document.getElementById('attoric-navigate-progress');
		if (bar) {
			return bar;
		}

		bar = document.createElement('div');
		bar.id = 'attoric-navigate-progress';
		bar.className = 'attoric-navigate-progress';
		bar.setAttribute('aria-hidden', 'true');
		document.body.appendChild(bar);

		return bar;
	};

	const hide = () => {
		if (hideTimeout) {
			clearTimeout(hideTimeout);
			hideTimeout = null;
		}

		if (removeTimeout) {
			clearTimeout(removeTimeout);
			removeTimeout = null;
		}

		const bar = document.getElementById('attoric-navigate-progress');
		if (!bar) {
			return;
		}

		bar.classList.remove('is-visible');
		removeTimeout = setTimeout(() => {
			bar.remove();
			removeTimeout = null;
		}, 200);
	};

	const show = () => {
		if (!document.body) {
			return;
		}

		if (removeTimeout) {
			clearTimeout(removeTimeout);
			removeTimeout = null;
		}

		const bar = getProgressBar();
		requestAnimationFrame(() => {
			bar.classList.add('is-visible');
		});

		if (hideTimeout) {
			clearTimeout(hideTimeout);
		}

		hideTimeout = setTimeout(hide, 15000);
	};

	document.addEventListener('livewire:navigate', show);
	document.addEventListener('livewire:navigating', show);
	document.addEventListener('livewire:navigated', hide);
	window.addEventListener('pageshow', hide);
};

const scrollToPartnerHashTarget = (hash) => {
	const id = String(hash || '').replace(/^#/, '');
	if (id === '' || /[^A-Za-z0-9_-]/.test(id)) {
		return false;
	}

	const heading = document.getElementById(id);
	if (! heading) {
		return false;
	}

	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	if (typeof heading.focus === 'function') {
		heading.focus({ preventScroll: true });
	}

	heading.scrollIntoView({
		behavior: reduceMotion ? 'auto' : 'smooth',
		block: 'start',
	});

	return true;
};

const isPartnerHashScrollPath = (pathname) => {
	return pathname === '/partner/dashboard'
		|| pathname === '/partner/setup'
		|| pathname === '/partner/cash';
};

const setupPartnerHashScroll = () => {
	if (window.__attoricPartnerHashScrollInitialized) {
		return;
	}

	window.__attoricPartnerHashScrollInitialized = true;

	const scrollLocationHash = () => {
		if (! isPartnerHashScrollPath(window.location.pathname) || window.location.hash === '#main-content') {
			return;
		}

		scrollToPartnerHashTarget(window.location.hash);
	};

	document.addEventListener('click', (event) => {
		const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
		if (! link) {
			return;
		}

		let url;
		try {
			url = new URL(link.getAttribute('href') || '', window.location.origin);
		} catch {
			return;
		}

		if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash === '') {
			return;
		}

		if (! isPartnerHashScrollPath(url.pathname) || url.hash === '#main-content') {
			return;
		}

		if (! scrollToPartnerHashTarget(url.hash)) {
			return;
		}

		event.preventDefault();
		if (window.location.hash !== url.hash) {
			history.pushState(null, '', url.hash);
		}
	});

	document.addEventListener('DOMContentLoaded', scrollLocationHash);
	document.addEventListener('livewire:navigated', scrollLocationHash);
	window.addEventListener('hashchange', scrollLocationHash);
};

setupPartnerHashScroll();

setupNavigateProgress();

const trackViewItem = () => {
	const element = document.querySelector('[data-ga-view-item="true"]');
	if (!element || typeof window.gtag !== 'function') {
		return;
	}

	const currency = element.dataset.gaCurrency;
	const itemId = element.dataset.gaItemId;
	const itemName = element.dataset.gaItemName;
	const itemPrice = Number(element.dataset.gaItemPrice);

	if (!currency || !itemId || !itemName || Number.isNaN(itemPrice)) {
		return;
	}

	window.gtag('event', 'view_item', {
		currency,
		value: itemPrice,
		items: [
			{
				item_id: itemId,
				item_name: itemName,
				price: itemPrice,
				quantity: 1,
			},
		],
	});
};

document.addEventListener('DOMContentLoaded', () => {
	bootPaymentAlpine();
	trackViewItem();
});

window.addEventListener('analytics-loaded', () => {
	trackViewItem();
});

bootPaymentAlpine();
